// src/templates/public-portal/variants/SeiaPortal.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { Service, Company, Product, Bundle, API_BS_URL} from '../../../services/api';
import {
  User, FileText,  Package, Menu, Crown, Lock, ArrowRight, ArrowLeft, ArrowUpRight, Layers, Settings, Utensils, Briefcase, PhoneCall, Heart, ChevronDown, ShoppingBag, ChevronLeft, ChevronRight, Play,Plus, Trash2,
  CheckCircle, Info, CheckCircle2, CreditCard, UtensilsCrossed, ShoppingCart, X,
  Search, Calendar, Minus, Users, Scale, Check, User2, Eye,
  Zap,
  Linkedin,
  Instagram,
  Facebook,
  Leaf,
  Target,
  ShieldCheck,
  Star,
  Quote,
  Globe,
  Shield,
  Compass,
  Award,
  ShieldAlert,
  Download,
  BookOpen
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../../services/api';

const SERVER_BASE_URL = import.meta.env.VITE_API_BS_URL || 'http://localhost:5000';

// Helper function to convert relative paths to absolute URLs
const getImageUrl = (imagePath: string): string => {
  if (!imagePath) return '';
  if (imagePath.startsWith('http')) return imagePath; // Already absolute
  return `${SERVER_BASE_URL}${imagePath}`; // Convert relative to absolute
};

interface SeiaProps {
  company: Company;
  slug: string;
  services: Service[];
  products?: Product[];
  bundles?: Bundle[];
  portalContent?: any;
}

type CatalogType = 'services' | 'products' | 'bundles';

type CartItem = {
  itemId: string;
  type: CatalogType;
  quantity: number;
  name: string;
  price: number;
  image?: string;
  madeToOrder?: boolean;
  orderPrice?: number;
  deliveryDays?: number;
  wantsOrder?: boolean;
};

interface ClientInfo {
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  taxId: string;
  vatNumber?: string;
  billingAddress: {
    street: string;
    city: string;
    country: string;
  };
}

const SeiaPortal: React.FC<SeiaProps> = ({ 
  company, 
  slug, 
  services: initialServices,
  products: initialProducts,
  bundles: initialBundles,
  portalContent = {
   hero: {
    headline: "SEIA LAW — Strategic Legal Counsel",
    subheadline: "Assessoria Jurídica Corporativa, Inteligência Estratégica e Representação de Alto Nível",
    backgroundImage: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop"
  },
    about: {
    enabled: true,
    title: "Excelência jurídica e visão estratégica para negócios globais",
    body: "<p>Com uma atuação pautada pela alta precisão técnica e rigor ético, a SEIA LAW presta consultoria jurídica especializada para corporações, instituições financeiras e investidores de alto impacto. Combinamos profundo conhecimento do ambiente regulatório com estratégias inovadoras para proteger ativos, gerir riscos e maximizar oportunidades de negócios complexos.</p>",
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2069&auto=format&fit=crop"
  },
    missionVision: {
      enabled: true,
      mission: { 
      title: "Nossa Missão", 
      content: "Oferecer soluções jurídicas sofisticadas e de alta performance, garantindo segurança institucional, governança e valor estratégico contínuo para nossos clientes." 
    },
      vision: { 
      title: "Nossa Visão", 
      content: "Ser a firma de advocacia de referência em Moçambique e no cenário internacional em operações de alta complexidade, governança e arbitragem." 
    },
     values: { 
      title: "Nossos Valores", 
      items: ["Integridade Inegociável", "Rigor Técnico", "Visão Estratégica", "Confidencialidade"] 
    }
  },
    clients: { enabled: true, items: [] },
    testimonials: { enabled: true, items: [] }
  },
}) => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeCatalog, setActiveCatalog] = useState<CatalogType>('services');
  const [searchTerm, setSearchTerm] = useState('');
  const [services, setServices] = useState<Service[]>(initialServices || []);
const values = [
    { 
      title: "Excelência", 
      desc: "Rigor técnico e compromisso inabalável com resultados de alto nível.",
      icon: Award 
    },
    { 
      title: "Estratégia", 
      desc: "Soluções jurídicas sob medida alinhadas aos objetivos de negócios do cliente.",
      icon: Compass 
    },
    { 
      title: "Integridade", 
      desc: "Relações de longo prazo construídas com total transparência e ética.",
      icon: Shield 
    },
    { 
      title: "Inovação", 
      desc: "Tecnologia de ponta ao serviço da eficiência e acompanhamento de processos.",
      icon: Zap 
    }
  ];
  // will calculate available catalogs once services/products/bundles are defined later

  // track searches performed by visitors (debounced so we don't spam the API)
   // logging de pesquisa já não depende de activeCatalog
  useEffect(() => {
    if (!searchTerm || !slug) return;
   const handler = setTimeout(async () => {
  try {
    await api.public.logSearch(slug, { term: searchTerm, catalog: 'all' });
    console.log(`✅ Pesquisa registrada: "${searchTerm}"`);
  } catch (err) {
    console.error('❌ Falha ao registrar busca:', err);
  }
}, 500);
    return () => clearTimeout(handler);
  }, [searchTerm, slug]);

  const [products, setProducts] = useState<Product[]>(initialProducts || []);
  const [bundles, setBundles] = useState<Bundle[]>(initialBundles || []);

  // compute which catalogs currently have items
  const availableCatalogs: CatalogType[] = React.useMemo(() => {
    const list: CatalogType[] = [];
    if (services && services.length > 0) list.push('services');
    if (products && products.length > 0) list.push('products');
    if (bundles && bundles.length > 0) list.push('bundles');
    return list;
  }, [services, products, bundles]);

 useEffect(() => {
  console.log("Company data received:", {
    hasBankAccounts: !!company?.bankAccounts?.length,
    hasMobileWallets: !!company?.mobileWallets,
    bankAccounts: company?.bankAccounts,
    mobileWallets: company?.mobileWallets
  });
}, [company]);

  // ensure activeCatalog is always one of the available ones when data changes
  useEffect(() => {
    if (availableCatalogs.length === 0) return;
    if (!availableCatalogs.includes(activeCatalog)) {
      setActiveCatalog(availableCatalogs[0]);
    }
  }, [availableCatalogs, activeCatalog]);

  const [submitting, setSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isSearchOpen, setIsSearchOpen] = useState(false);


  const [client, setClient] = useState<ClientInfo>({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    taxId: '',
    vatNumber: '',
    billingAddress: { street: '', city: '', country: 'Moçambique' }
  });

  const [requestedInstallments, setRequestedInstallments] = useState(1);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [requestIntent, setRequestIntent] = useState<'quotation' | 'invoice'>('quotation');
  const [notes, setNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'mpesa'|'emola'|'visa'|'transfer'|'none'>('none');
  const [mobileMoneyPhone, setMobileMoneyPhone] = useState('');
  const [showAwaitingConfirmation, setShowAwaitingConfirmation] = useState(false);
  const [awaitingRef, setAwaitingRef] = useState<string>('');
  const [pollStatus, setPollStatus] = useState<'waiting'|'confirmed'|'failed'>('waiting');
  const [pollAttempts, setPollAttempts] = useState(0);
const [visibleServices, setVisibleServices] = useState(3);
const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const currency = company.currency || 'MT';
  const [currentLang, setCurrentLang] = useState('EN');

    // ------------ alterações -------------
  // controla o “ver mais” de cada secção
  const [showAllProducts, setShowAllProducts] = useState(false);


  // guarda o tipo do item que está aberto no modal (usado mais abaixo)
  const [selectedItemType, setSelectedItemType] = useState<CatalogType | null>(null);

  const openItem = (item: any, type: CatalogType) => {
    setSelectedItem(item);
    setSelectedItemType(type);
    setCarouselIndex(0);
  };

    // listas filtradas individualmente
  const filteredProducts = products.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description?.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  const filteredServices = services.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description?.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  const filteredBundles = bundles.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description?.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.shortDescription?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Only load if initial data is not provided (public portal provides all data)
 useEffect(() => {
  if (initialServices?.length && initialProducts?.length && initialBundles?.length) {
    return;
  }
  
  const loadCatalog = async () => {
    try {
      const promises = [];
      if (!initialServices?.length) promises.push(api.services.getAll());
      if (!initialProducts?.length) promises.push(api.products.getAll());
      if (!initialBundles?.length) promises.push(api.bundles.getAll());
      
      if (promises.length === 0) return;
      
      const results = await Promise.all(promises);
      console.log("Produtos vindos da API:", results[1]);
      // LOGS DE DEPURAÇÃO
      console.log("--- Resposta Bruta da API ---");
      console.log("Serviços:", results[0]);
      console.log("Produtos:", results[1]);
      console.log("Bundles (Combos/Subs):", results[2]);
      
      // Verifica se os campos esperados existem dentro do primeiro bundle (se houver)
      if (results[2] && results[2].length > 0) {
        console.log("Estrutura do primeiro Bundle:", results[2][0]);
        console.log("Itens populados?", results[2][0].items);
      }

      if (!initialServices?.length && results[0]) setServices(results[0]);
      if (!initialProducts?.length && results[1]) setProducts(results[1]);
      if (!initialBundles?.length && results[2]) setBundles(results[2]);
      
    } catch (err) {
      console.error('Erro detalhado ao carregar catálogo:', err);
    }
  };
  loadCatalog();
}, []);
  // Reset carousel index whenever a new item is selected
  useEffect(() => {
    setCarouselIndex(0);
  }, [selectedItem]);

 const total = useMemo(() => {
  const subtotal = cart.reduce((acc, item) => acc + (item.price * item.quantity), 0);
  
  // Calculamos quanto custa apenas as taxas de encomenda (se houver)
  const orderFeesTotal = cart.reduce((acc, item) => {
    if (item.wantsOrder && item.orderPrice) {
      return acc + (Number(item.orderPrice) * item.quantity);
    }
    return acc;
  }, 0);

  // Se houver qualquer item com taxa de encomenda ativa, o Grand Total vira apenas o valor das taxas
  // Caso contrário, é o subtotal normal.
  const isPayingFees = cart.some(item => item.wantsOrder && Number(item.orderPrice) > 0);
  const grandTotal = isPayingFees ? orderFeesTotal : subtotal;

  return { subtotal, orderFeesTotal, grandTotal, isPayingFees };
}, [cart]);



  // Image helper (products & bundles have real images, services use placeholder – add image field to Service later)
  const getItemImage = (item: any, type: CatalogType): string | undefined => {
    // Products and bundles keep previous logic. For services, prefer real images from DB if available.
    if (type === 'products' && item.images?.length > 0) return getImageUrl(item.images[0]);
    if (type === 'bundles' && item.image) return getImageUrl(item.image);
    if (type === 'services' && item.images?.length > 0) return getImageUrl(item.images[0]);
    return undefined;
  };

  const getItemPrice = (item: any): number => {
  if (!item) return 0;

  const price = 
    item.billingPricePerCycle ?? 
    item.basePrice ?? 
    item.price ?? 
    0;

  // Debug opcional (remove depois de testar)
  // console.log(`getItemPrice for "${item.name}" (type: ${item.type || 'unknown'}):`, price);

  return Number(price);
};

  function getItemDescription(item: any): string {
    return 'shortDescription' in item ? (item as any).shortDescription || '' :
      ('description' in item ? (item as any).description || '' : '');
  }

  // Cart actions
const addToCart = (item: any, type: CatalogType) => {
  // Proteção contra stock zero (exceto madeToOrder)
  if (item.stockQuantity <= 0 && !item.madeToOrder) {
    toast.error(`Produto ${item.name} sem stock disponível`);
    return;
  }

  const existingIndex = cart.findIndex(c => c.itemId === item._id);

  if (existingIndex !== -1) {
    const updated = [...cart];
    updated[existingIndex].quantity += 1;
    setCart(updated);
  } else {
    setCart([...cart, {
      itemId: item._id,
      type,
      quantity: 1,
      name: item.name,
      price: getItemPrice(item),
      image: getItemImage(item, type),

      // === CAMPOS CRUCIAIS PARA MADE TO ORDER ===
      madeToOrder: item.madeToOrder, 
    orderPrice: item.orderPrice,
    deliveryDays: item.deliveryDays,
      wantsOrder: false,                          // começa desmarcado
    }]);
  }

  toast.success(`${item.name} adicionado ao carrinho`, { position: 'top-center' });
};

   const hasOrderWithPrice = cart.some(item => 
  item.wantsOrder === true && Number(item.orderPrice || 0) > 0
);

  const updateCartQuantity = (itemId: string, newQty: number) => {
    if (newQty < 1) return;
    setCart(cart.map(item => item.itemId === itemId ? { ...item, quantity: newQty } : item));
  };

  const removeFromCart = (itemId: string) => {
    setCart(cart.filter(item => item.itemId !== itemId));
  };

  // Totals (exactly your original logic)
  const calculateTotals = () => {
    let subtotal = 0;
    let minAllowed = 99;
    let maxPenaltyPct = 0;

    cart.forEach(cartItem => {
      subtotal += cartItem.price * cartItem.quantity;
      const svc = services.find(s => s._id === cartItem.itemId);
      if (svc) {
        minAllowed = Math.min(minAllowed, svc.allowedInstallments || 3);
        maxPenaltyPct = Math.max(maxPenaltyPct, svc.penaltyPercentagePerInstallment || 0);
      }
    });

    let penalty = 0;
    if (requestedInstallments > minAllowed) {
      const extra = requestedInstallments - minAllowed;
      penalty = subtotal * (extra * (maxPenaltyPct / 100));
    }

    return { subtotal, penalty, grandTotal: subtotal + penalty };
  };

  const totals = calculateTotals();

  // Dynamic max installments (your original logic)
  const getMaxAllowedInstallments = (): number => {
    let minAllowed = Infinity;
    let hasService = false;
    cart.forEach(cartItem => {
      const svc = services.find(s => s._id === cartItem.itemId);
      if (svc) {
        hasService = true;
        minAllowed = Math.min(minAllowed, svc.allowedInstallments || 3);
      }
    });
    return hasService ? Math.max(1, minAllowed) : 12;
  };

  const hasValidItems = cart.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasValidItems) {
      toast.error('Adicione pelo menos um item ao carrinho');
      return;
    }
    if (!client.name || !client.email) {
      toast.error('Nome da empresa e email são obrigatórios');
      return;
    }

    setSubmitting(true);

    try {
     // Dentro de handleSubmit, substitui o payload por:
      const payload = {
        companyId: company._id,
        clientData: { ...client, origin: 'external' },
        requisitionData: {
          items: cart.map(cartItem => ({
            itemType: cartItem.type === 'services' ? 'service' : 
                      cartItem.type === 'products' ? 'product' : 'bundle',
            item: cartItem.itemId,
            isMadeToOrder: cartItem.wantsOrder,
            priceAtOrder: cartItem.price,
            feePaid: cartItem.wantsOrder ? cartItem.orderPrice : 0,
            quantity: cartItem.quantity
          })),
          paymentMethod,
          totalAmount: total.subtotal,
          amountPaid: total.isPayingFees ? total.orderFeesTotal : 0,
          requestedInstallments,
          deliveryDate,
          requestIntent,
          notes
        }
      };

      await api.requisitions.submitPublic(payload);

      setShowSuccessModal(true);
      toast.success('Requisição enviada com sucesso!');

      // Reset everything
      setCart([]);
      setClient({
        name: '', contactPerson: '', email: '', phone: '', taxId: '', vatNumber: '',
        billingAddress: { street: '', city: '', country: 'Moçambique' }
      });
      setRequestedInstallments(1);
      setDeliveryDate('');
      setRequestIntent('quotation');
      setNotes('');
      setIsCartOpen(false);
    } catch (err: any) {
      toast.error(err.message || 'Falha ao enviar');
    } finally {
      setSubmitting(false);
    }
  };
useEffect(() => {
  if (selectedItem?.items) {
    console.log("ESTRUTURA FINAL NO FRONTEND:", JSON.stringify(selectedItem.items, null, 2));
  }
}, [selectedItem]);
 const handlePayment = async () => {
  if (!paymentMethod || paymentMethod === 'none') {
    toast.error('Selecione um método de pagamento');
    return;
  }
  if (!hasValidItems) {
    toast.error('Adicione pelo menos um item ao carrinho');
    return;
  }
  if (!client.name || !client.email) {
    toast.error('Preencha o nome e email para processar o pagamento');
    return;
  }

  // Mobile money prefix validation
  if (paymentMethod === 'mpesa' || paymentMethod === 'emola') {
    if (!mobileMoneyPhone) {
      toast.error(`Número de ${paymentMethod === 'mpesa' ? 'M-Pesa' : 'E-Mola'} é obrigatório`);
      return;
    }
    const cleaned = mobileMoneyPhone.replace(/\D/g, '');
    const isMpesa = paymentMethod === 'mpesa';
    const isEmola = paymentMethod === 'emola';
    const validMpesa = cleaned.startsWith('84') || cleaned.startsWith('85');
    const validEmola = cleaned.startsWith('86') || cleaned.startsWith('87');
    if (isMpesa && !validMpesa) {
      toast.error('Número M-Pesa deve começar com 84 ou 85');
      return;
    }
    if (isEmola && !validEmola) {
      toast.error('Número E-Mola deve começar com 86 ou 87');
      return;
    }
  }

  setSubmitting(true);
  try {
    const effectivePhone = (paymentMethod === 'mpesa' || paymentMethod === 'emola')
      ? mobileMoneyPhone : client.phone;
    const payload = {
      totalAmount: totals.grandTotal,
      method: paymentMethod,
      customer: {
        name: client.name,
        phone: effectivePhone,
        email: client.email,
      },
      companyId: company._id,
      currency: currency,
      items: cart.map(ci => ({
        itemId: ci.itemId,
        name: ci.name,
        quantity: ci.quantity,
        price: ci.price,
        type: ci.type
      }))
    };
    const resp = await api.checkout.process(payload, true);
    console.log('✅ Resposta do checkout:', resp);
    if (resp?.success) {
      if (resp.awaiting_confirmation || (resp.status === 'pending' && (paymentMethod === 'mpesa' || paymentMethod === 'emola'))) {
        setAwaitingRef(resp.externalRef);
        setPollStatus('waiting');
        setPollAttempts(0);
        setShowAwaitingConfirmation(true);
        return;
      }
      if (resp.url) {
        window.location.href = resp.url;
      } else {
        toast.success(resp.message || 'Pagamento processado com sucesso!');
        setTimeout(() => {
          window.location.href = `/order-success?ref=${resp.externalRef}`;
        }, 1500);
      }
    } else {
      toast.error(resp?.message || 'Não foi possível iniciar o pagamento');
    }
  } catch (err: any) {
    console.error('Payment error:', err);
    if (err.message?.includes('504') || err.response?.status === 504) {
      toast.error('O serviço de pagamento está demorando muito. Tente novamente.');
    } else {
      toast.error(err.message || 'Erro ao processar pagamento');
    }
  } finally {
    setSubmitting(false);
  }
};

    // Poll awaiting confirmation
  useEffect(() => {
    if (!showAwaitingConfirmation || !awaitingRef) return;
    let cancelled = false;
    setPollStatus('waiting');
    setPollAttempts(0);

    const interval = setInterval(async () => {
      try {
        const res = await api.checkout.transactionStatus(awaitingRef);
        if (cancelled) return;
        if (res?.found && res.transaction) {
          if (res.transaction.status === 'success') {
            setPollStatus('confirmed');
            clearInterval(interval);
            setTimeout(() => {
              setShowAwaitingConfirmation(false);
              setShowSuccessModal(true);
            }, 1500);
            return;
          } else if (res.transaction.status === 'failed') {
            setPollStatus('failed');
            clearInterval(interval);
            setTimeout(() => setShowAwaitingConfirmation(false), 3000);
            return;
          }
        } else if (res?.status === 'success' || res?.status === 'completed') {
          setPollStatus('confirmed');
          clearInterval(interval);
          setTimeout(() => {
            window.location.href = `/order-success?ref=${awaitingRef}`;
          }, 2000);
          return;
        } else if (res?.status === 'failed' || res?.status === 'cancelled') {
          setPollStatus('failed');
          clearInterval(interval);
          return;
        }
        setPollAttempts(p => p + 1);
      } catch {}
    }, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [showAwaitingConfirmation, awaitingRef]);

  const closeSuccessModal = () => setShowSuccessModal(false);
const navLinks = [
    { name: 'Sobre Nós', href: '#about' },
    { name: 'Serviços', href: '#services' },
    { name: 'Produtos', href: '#invest' },
    { name: 'International Desk', href: '#international' },
  
  ];
  const whyChooseUs = [
    "Conhecimento profundo do mercado moçambicano",
    "Experiência em operações empresariais e M&A",
    "Abordagem estratégica para investidores globais",
    "Padrões internacionais de serviço e governança"
  ];
  // Valores oficiais do briefing da SEIA LAW
  const defaultValues = [
    { name: "Excelência", detail: "Rigor técnico & compromisso com resultados" },
    { name: "Integridade", detail: "Confiança, ética e total transparência" },
    { name: "Estratégia", detail: "Soluções alinhadas aos objetivos de negócio" },
    { name: "Inovação", detail: "Tecnologia a favor da agilidade e rigor" }
  ];

  // Áreas de Prática Padrão do Briefing da SEIA LAW
  const defaultPracticeAreas = [
    {
      _id: 'corp-inv',
      name: 'Corporate & Investment',
      description: 'Estruturação de negócios, investimento estrangeiro e operações empresariais estratégicas.',
      icon: Briefcase,
      badge: 'Core Practice',
      images: ['https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1000&auto=format&fit=crop']
    },
    {
      _id: 'mining-energy',
      name: 'Mining & Energy',
      description: 'Assessoria jurídica especializada para recursos naturais, transição energética e sectores estratégicos.',
      icon: Zap,
      badge: 'Strategic Sector',
      images: ['https://images.unsplash.com/photo-1578575437130-527eed3abbec?q=80&w=1000&auto=format&fit=crop']
    },
    {
      _id: 'labour-emp',
      name: 'Labour & Employment',
      description: 'Soluções laborais abrangentes e gestão de capital humano para empresas e executivos.',
      icon: Users,
      images: ['https://images.unsplash.com/photo-1521737711867-e3b97375f902?q=80&w=1000&auto=format&fit=crop']
    },
    {
      _id: 'contracts-trans',
      name: 'Contracts & Transactions',
      description: 'Negociação, elaboração e revisão de contratos complexos de âmbito nacional e internacional.',
      icon: FileText,
      images: ['https://images.unsplash.com/photo-1450133064473-71024230f91b?q=80&w=1000&auto=format&fit=crop']
    },
    {
      _id: 'compliance',
      name: 'Compliance',
      description: 'Gestão de riscos regulatórios, prevenção da corrupção e programas de conformidade corporativa.',
      icon: ShieldAlert,
      images: ['https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=1000&auto=format&fit=crop']
    },
    {
      _id: 'dispute-res',
      name: 'Dispute Resolution',
      description: 'Prevenção, contencioso e resolução alternativa e estratégica de litígios e conflitos comerciais.',
      icon: Scale,
      images: ['https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=1000&auto=format&fit=crop']
    }
  ];
  const servicesToDisplay = filteredServices.length > 0 ? filteredServices : defaultPracticeAreas;
 const defaultResources = [
    {
      _id: 'res-1',
      name: 'Mozambique Legal & Investment Guide',
      category: 'Strategic Guide',
      description: 'Análise detalhada do enquadramento legal, incentivos fiscais e regulamentação do investimento estrangeiro em Moçambique.',
      images: ['https://images.unsplash.com/photo-1450133064473-71024230f91b?q=80&w=1000&auto=format&fit=crop'],
      isFeatured: true
    },
    {
      _id: 'res-2',
      name: 'Energy & Mining Regulatory Outlook',
      category: 'Sector Report',
      description: 'Panorama regulatório para o sector extractivo, licenças ambientais e conformidade com o conteúdo local.',
      images: ['https://images.unsplash.com/photo-1578575437130-527eed3abbec?q=80&w=1000&auto=format&fit=crop']
    },
    {
      _id: 'res-3',
      name: 'Corporate Labor Law Framework',
      category: 'Compliance Brief',
      description: 'Guia de contratação, regimes de trabalho para expatriados e gestão de riscos laborais segundo a Lei do Trabalho.',
      images: ['https://images.unsplash.com/photo-1521737711867-e3b97375f902?q=80&w=1000&auto=format&fit=crop']
    },
    {
      _id: 'res-4',
      name: 'Dispute Resolution & Arbitration Brief',
      category: 'Legal Insights',
      description: 'Mecanismos de resolução alternativa de litígios e arbitragem comercial em jurisdições africanas.',
      images: ['https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=1000&auto=format&fit=crop']
    }
  ];

  const itemsToDisplay = filteredProducts.length > 0 ? filteredProducts : defaultResources;
 const defaultLegalBundles = [
    {
      _id: 'bundle-1',
      name: 'Investor Landing & Setup Package',
      type: 'Combo',
      description: 'Solução 360° para investidores estrangeiros: constituição de empresa, licenciamento, vistos de trabalho e estruturação fiscal em Moçambique.',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1000&auto=format&fit=crop',
      features: ['Registo Comercial & Fiscal', 'Apoio em Vistos & Imigração', 'Contratos de Instalação/Arrendamento', 'Aconselhamento Regulatório Inicial'],
      price: 4500
    },
    {
      _id: 'bundle-2',
      name: 'Corporate & Regulatory Retainer',
      type: 'Combo',
      description: 'Acompanhamento jurídico mensal contínuo para operações de médio e grande porte, garantindo conformidade laboral, contratual e fiscal.',
      image: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=1000&auto=format&fit=crop',
      features: ['Revisão Ilimitada de Contratos', 'Apoio Laboral & RH Contínuo', 'Auditorias de Compliance Trimestrais', 'Suporte Directo a Executivos'],
      price: 3200
    },
    {
      _id: 'bundle-3',
      name: 'Mining & Energy Compliance Suite',
      type: 'Combo',
      description: 'Pacote especializado para empresas do sector extractivo e de energia: licenças ambientais, cumprimento de conteúdo local e due diligence.',
      image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?q=80&w=1000&auto=format&fit=crop',
      features: ['Due Diligence Concessionária', 'Planos de Conteúdo Local (Local Content)', 'Gestão de Licenças Ambientais', 'Auditoria Contratual de Sector'],
      price: 6000
    }
  ];

  // Filtra itens do tipo Combo ou usa os planos padrão
  const comboBundles = filteredBundles.filter(b => b.type === 'Combo');
  const displayBundles = comboBundles.length > 0 ? comboBundles : defaultLegalBundles;
 const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'yearly'

  // Planos de Subscrição/Retainer Padrão para a SEIA LAW (fallback)
  const defaultSubscriptions = [
    {
      _id: 'sub-advisory',
      name: 'Advisory Retainer',
      type: 'Subscription',
      description: 'Ideal para PMEs e startups que necessitam de aconselhamento jurídico contínuo e gestão de riscos preventivos.',
      monthlyPrice: 1200,
      yearlyPrice: 12000,
      popular: false,
      includedLimits: [
        { description: 'Até 10 horas/mês de consultoria directa' },
        { description: 'Análise e elaboração de contratos normais' },
        { description: 'Suporte em conformidade laboral básica' },
        { description: 'Relatório regulatório trimestral' }
      ]
    },
    {
      _id: 'sub-corporate',
      name: 'Corporate General Counsel',
      type: 'Subscription',
      description: 'Acompanhamento integrado para empresas consolidadas que procuram um departamento jurídico externo e especializado.',
      monthlyPrice: 3500,
      yearlyPrice: 35000,
      popular: true,
      includedLimits: [
        { description: 'Até 30 horas/mês de apoio especializado' },
        { description: 'Revisão e negociação de contratos complexos' },
        { description: 'Auditorias continuadas de Compliance & Fiscal' },
        { description: 'Apoio em vistos de trabalho para executivos' },
        { description: 'Acesso prioritário a equipa sênior' }
      ]
    },
    {
      _id: 'sub-enterprise',
      name: 'Strategic Enterprise',
      type: 'Subscription',
      description: 'Estrutura personalizada para grandes multinacionais, projectos de Energia/Mineração e M&A de alto impacto.',
      monthlyPrice: 7500,
      yearlyPrice: 75000,
      popular: false,
      includedLimits: [
        { description: 'Horas ilimitadas de apoio em transacções' },
        { description: 'Sócio Dedicado (Dedicated Senior Partner)' },
        { description: 'Representação em arbitragem e contencioso' },
        { description: 'Assessoria em Relações Governamentais' },
        { description: 'Auditoria de Conteúdo Local e Licenciamento' }
      ]
    }
  ];

  // Filtra itens do tipo Subscription ou usa os planos padrão
  const subscriptionBundles = filteredBundles.filter(item => item.type === 'Subscription');
  const displayPlans = subscriptionBundles.length > 0 ? subscriptionBundles : defaultSubscriptions;
  return (
    <div className="min-h-screen bg-[#f8f7f2] text-[#1a1a1a] font-sans overflow-x-hidden">
      <style>{`
        :root {
          --primary: #0E7D83;
          --primary-dark: #0a5e63;
          --primary-light: #2daab0;
          --primary-subtle: #e0f2f2;
          --accent: #E5E65D;
          --accent-dark: #c4c53d;
          --bg: #f8f7f2;
          --surface: #ffffff;
          --fg: #1a1a1a;
          --muted: #6b7280;
          --border: #e5e7eb;
          --border-light: #f3f4f6;
          --decor: #0a5e63;
        }
        @media (max-width: 768px) {
          .mobile-clip-none {
            clip-path: none !important;
            border-radius: 1.5rem !important;
          }
          .mobile-stack {
            flex-direction: column !important;
          }
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

    <div className="bg-[#111827] text-white font-sans selection:bg-[#D4AF37] selection:text-black">
      {/* Top Banner - International Desk / Announcement */}
      <div className="bg-[#06241A] py-2 px-4 border-b border-[#0B4332]/50 text-center text-[10px] md:text-xs text-emerald-100/70 tracking-widest uppercase flex items-center justify-center gap-2">
        <span className="inline-block w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse" />
        <span>Legal Gateway to Mozambique • Global Perspective & Local Excellence</span>
      </div>

      {/* Header */}
      <header className="sticky top-0 w-full z-[60] bg-[#093829]/95 backdrop-blur-md border-b border-emerald-900/40 transition-all">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between relative">
          
          {/* Left: Mobile Menu Toggle + Logo */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center bg-[#0B4332] rounded-lg border border-emerald-700/30 text-emerald-100 hover:bg-emerald-800 transition-colors"
              aria-label="Toggle Menu"
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>

            {/* Brand / Logo */}
            <a href="#" className="flex items-center gap-3 group">
              {company.logo ? (
                <img src={company.logo} alt={company.name} className="h-7 md:h-9 w-auto object-contain" />
              ) : (
                <div className="flex flex-col">
                  <span className="font-serif text-xl md:text-2xl font-bold tracking-wider text-white group-hover:text-[#D4AF37] transition-colors">
                    SEIA <span className="text-[#D4AF37]">LAW</span>
                  </span>
                  <span className="text-[8px] md:text-[9px] tracking-[0.25em] text-emerald-300 uppercase -mt-1">
                    Mozambique
                  </span>
                </div>
              )}
            </a>
          </div>

          {/* Center Nav (Desktop) */}
          <nav className={`hidden lg:flex items-center gap-1 xl:gap-2 transition-opacity duration-300 ${isSearchOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
            {navLinks.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="px-4 py-2 rounded-full text-xs xl:text-sm font-medium text-emerald-100/90 hover:text-white hover:bg-[#0B4332] transition-all whitespace-nowrap"
              >
                {item.name}
              </a>
            ))}
          </nav>

          {/* Right: Search, Language & CTA */}
          <div className="flex items-center gap-2 md:gap-4 z-10">
            {/* Search Toggle */}
            <div className="flex items-center">
              {!isSearchOpen ? (
                <button 
                  onClick={() => setIsSearchOpen(true)} 
                  className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-[#0B4332] border border-emerald-700/30 rounded-full text-emerald-200 hover:text-white hover:border-[#D4AF37] transition-all"
                  aria-label="Search"
                >
                  <Search size={17} />
                </button>
              ) : (
                <div className="flex items-center gap-2 bg-[#06241A] border border-[#D4AF37]/50 px-3 py-1.5 rounded-full animate-in fade-in zoom-in-95 duration-200 w-[200px] sm:w-[280px] shadow-2xl">
                  <Search size={16} className="text-[#D4AF37] shrink-0" />
                  <input 
                    autoFocus
                    type="text"
                    placeholder="Search practices, insights..."
                    className="bg-transparent border-none outline-none text-xs w-full text-white placeholder:text-emerald-300/50"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button onClick={() => { setIsSearchOpen(false); setSearchTerm(''); }} className="text-emerald-300 hover:text-white shrink-0">
                    <X size={16} />
                  </button>
                </div>
              )}
            </div>

            {/* Language Desk Switcher */}
            <div className="hidden sm:flex items-center gap-1 border border-emerald-800/60 bg-[#06241A] rounded-full p-1 text-xs">
              <Globe size={13} className="ml-2 text-[#D4AF37]" />
              {['PT', 'EN'].map((lang) => (
                <button
                  key={lang}
                  onClick={() => setCurrentLang(lang)}
                  className={`px-2 py-0.5 rounded-full text-[10px] font-bold transition-all ${
                    currentLang === lang ? 'bg-[#D4AF37] text-black' : 'text-emerald-200/70 hover:text-white'
                  }`}
                >
                  {lang}
                </button>
              ))}
            </div>
 {/* Shopping Cart Button */}
              <button
                onClick={() => setIsCartOpen(true)}
                className="relative flex items-center gap-2 px-3.5 py-2 bg-[#053B29] hover:bg-[#042f21] text-white rounded-full transition-all shadow-sm"
              >
                <ShoppingBag size={18} />
                <span className="hidden sm:inline text-xs font-bold">Cart</span>
                {cart.length > 0 && (
                  <span className="bg-amber-400 text-slate-950 text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center ml-0.5">
                    {cart.length}
                  </span>
                )}
              </button>
            {/* Schedule Consultation Button */}
            {!isSearchOpen && (
              <a
                href="#contact"
                className="hidden sm:inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black font-semibold text-xs md:text-sm hover:brightness-110 transition-all shadow-md shadow-black/20 whitespace-nowrap"
              >
                Agendar consultoria
              </a>
            )}
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-emerald-900/50 bg-[#06241A] px-6 py-6 space-y-4 animate-in slide-in-from-top duration-300">
            <div className="flex flex-col gap-2">
              {navLinks.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-3 rounded-xl bg-[#093829] text-sm font-medium text-emerald-100 hover:text-white hover:bg-[#0B4332] transition-all border border-emerald-800/30"
                >
                  {item.name}
                </a>
              ))}
            </div>
            
            <div className="pt-2 flex items-center justify-between border-t border-emerald-900/60">
              <span className="text-xs text-emerald-300/70 flex items-center gap-2">
                <Globe size={14} /> Language
              </span>
              <div className="flex gap-2">
                {['PT', 'EN'].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setCurrentLang(lang)}
                    className={`px-3 py-1 rounded-lg text-xs font-bold border ${
                      currentLang === lang ? 'bg-[#D4AF37] text-black border-[#D4AF37]' : 'border-emerald-800 text-emerald-200'
                    }`}
                  >
                    {lang}
                  </button>
                ))}
              </div>
            </div>

            <a
              href="#contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-full text-center block py-3.5 rounded-xl bg-[#D4AF37] text-black font-bold text-sm hover:bg-[#C5A059] transition-all shadow-lg"
            >
              Agendar consultoria
            </a>
          </div>
        )}
      </header>

      {/* HERO SECTION */}
      <section className="relative bg-[#06241A] overflow-hidden py-12 md:py-20 lg:py-24 border-b border-emerald-900/50">
        {/* Subtle Background Mesh/Grid */}
        <div 
          className="absolute inset-0 opacity-5 pointer-events-none" 
          style={{
            backgroundImage: 'radial-gradient(#D4AF37 1px, transparent 1px)', 
            backgroundSize: '32px 32px'
          }} 
        />
        
        {/* Glowing Gradient Effect */}
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-[#0B4332] rounded-full blur-[128px] opacity-40 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            
            {/* Left Content Column */}
            <div className="lg:col-span-7 space-y-6 md:space-y-8 text-center lg:text-left">
              
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#093829] border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-medium uppercase tracking-wider">
                <ShieldCheck size={14} />
                <span>Soluções Jurídicas</span>
              </div>

              {/* Headline */}
              <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-white leading-[1.1] tracking-tight">
                {portalContent?.hero?.headline || (
                  <>
                    SEIA LAW <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-200 via-white to-[#D4AF37]">
                      Your Legal Gateway
                    </span> <br />
                    to Mozambique
                  </>
                )}
              </h1>

              {/* Subheadline */}
              <p className="text-emerald-100/70 max-w-2xl text-base md:text-xl leading-relaxed mx-auto lg:mx-0 font-light">
                {portalContent?.hero?.subheadline || 
                  "Strategic legal solutions for investors, businesses, and global institutions navigating Mozambique’s high-growth sectors."}
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-2">
                <a
                  href="#contact"
                  className="w-full sm:w-auto px-8 py-4 rounded-full bg-[#D4AF37] hover:bg-[#C5A059] text-black font-bold text-sm md:text-base transition-all shadow-lg shadow-[#D4AF37]/10 flex items-center justify-center gap-2 group"
                >
                  <span>Agendar consultoria</span>
                  <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>

                <a
                  href="#invest"
                  className="w-full sm:w-auto px-8 py-4 rounded-full bg-[#093829] hover:bg-[#0B4332] text-white border border-emerald-700/40 font-medium text-sm md:text-base transition-all flex items-center justify-center"
                >
                  Investir em Moçambique
                </a>
              </div>

              {/* Mini Features List */}
              <div className="pt-6 border-t border-emerald-900/60 grid grid-cols-2 sm:grid-cols-3 gap-4 text-left">
                <div>
                  <div className="text-lg md:text-xl font-serif font-bold text-white">Perspetiva Local</div>
                  <div className="text-xs text-emerald-300/60">Conhecimento Profundo do Mercado</div>
                </div>
                <div>
                  <div className="text-lg md:text-xl font-serif font-bold text-white">Alcance Global</div>
                  <div className="text-xs text-emerald-300/60">Balcão Internacional</div>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <div className="text-lg md:text-xl font-serif font-bold text-[#D4AF37]">Tecnologia Jurídica</div>
                  <div className="text-xs text-emerald-300/60">Portal Digital do Cliente</div>
                </div>
              </div>

            </div>

            {/* Right Media Column */}
            <div className="lg:col-span-5">
              <div className="relative mx-auto max-w-md lg:max-w-none">
                
                {/* Main Hero Image */}
                <div className="relative rounded-3xl overflow-hidden border border-emerald-700/30 shadow-2xl bg-[#093829] aspect-[4/5]">
                  <img 
                    src={getImageUrl?.(portalContent?.hero?.backgroundImage) || "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1200&auto=format&fit=crop"} 
                    alt="Maputo Corporate Infrastructure" 
                    className="w-full h-full object-cover object-center filter brightness-90 hover:scale-105 transition-transform duration-700" 
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#06241A] via-transparent to-transparent opacity-80" />
                  
                  {/* Floating Client Portal Badge / Card */}
                  <div className="absolute bottom-6 left-6 right-6 p-4 md:p-5 rounded-2xl bg-[#093829]/90 backdrop-blur-md border border-[#D4AF37]/30 shadow-xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase font-bold text-[#D4AF37] tracking-wider">Pré-visualização do Portal do Cliente</span>
                      <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                    </div>
                    <div className="text-xs font-semibold text-white">Grupo Internacional de Investimentos</div>
                    <div className="text-[11px] text-emerald-200/70 flex justify-between mt-1">
                      <span>Projeto: Licenciamento Empresarial</span>
                      <span className="text-emerald-400 font-medium">Em progresso</span>
                    </div>
                  </div>
                </div>

                {/* Decorative Frame Line */}
                <div className="absolute -inset-2 rounded-[32px] border border-[#D4AF37]/20 pointer-events-none -z-10 hidden sm:block" />
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>

      {/* OVERLAY DE PESQUISA */}
      {isSearchOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[55] pointer-events-none transition-opacity" />
      )}

      <section id="about" className="py-20 md:py-32 bg-[#093829] text-white relative overflow-hidden border-b border-emerald-900/40">
      {/* Background Subtle Mesh */}
      <div 
        className="absolute inset-0 opacity-5 pointer-events-none" 
        style={{
          backgroundImage: 'radial-gradient(#D4AF37 1px, transparent 1px)', 
          backgroundSize: '40px 40px'
        }} 
      />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        
        {/* Main Grid: Story & Visuals */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* Left Column: Text & Story */}
          <div className="lg:col-span-7 space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
                <span className="w-8 h-[1px] bg-[#D4AF37]" />
                <span>About SEIA LAW</span>
              </div>

              <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-white leading-tight">
                {portalContent?.about?.title || (
                  <>
                    Excelência Jurídica. <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-200 via-white to-[#D4AF37]">
                      Perspetiva Local. Visão Global.
                    </span>
                  </>
                )}
              </h2>

              <p className="text-emerald-100/80 text-base md:text-lg leading-relaxed font-light">
                {portalContent?.about?.body ? (
                  <span dangerouslySetInnerHTML={{ __html: portalContent.about.body }} />
                ) : (
                  "A SEIA LAW é uma firma jurídica moçambicana orientada para negócios, investimentos e soluções estratégicas. Nascemos com a visão de oferecer uma advocacia moderna, combinando profundo conhecimento local com os mais altos padrões internacionais para acompanhar investidores e grandes corporações."
                )}
              </p>
            </div>

            {/* Why SEIA LAW - Checklist */}
            <div className="pt-2">
              <h3 className="text-xs uppercase tracking-wider text-[#D4AF37] font-bold mb-4">
                Por que SEIA LAW
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 bg-[#06241A]/60 p-3 rounded-xl border border-emerald-800/40">
                    <div className="w-5 h-5 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37] flex items-center justify-center shrink-0 mt-0.5">
                      <Check size={12} className="text-[#D4AF37] stroke-[3]" />
                    </div>
                    <span className="text-xs md:text-sm text-emerald-100/90 font-medium leading-snug">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Action */}
            <div className="pt-4 flex items-center gap-4">
              <a
                href="#contact"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#D4AF37] hover:bg-[#C5A059] text-black font-bold text-sm transition-all shadow-xl shadow-black/20 group"
              >
                <span>Fale com Nossa Equipe</span>
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>

          {/* Right Column: Architectural / Corporate Image Composition */}
          <div className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              
              {/* Main Image Frame */}
              <div className="relative rounded-2xl overflow-hidden border border-emerald-700/40 shadow-2xl aspect-[4/5] bg-[#06241A]">
                <img 
                  src={getImageUrl?.(portalContent?.about?.image) || "https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=1200&auto=format&fit=crop"} 
                  alt="SEIA LAW Executive Team" 
                  className="w-full h-full object-cover filter brightness-95" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#06241A] via-transparent to-transparent opacity-80" />
              </div>

              {/* Stat Badge Overlay */}
              <div className="absolute -bottom-6 -left-6 bg-[#06241A] border border-[#D4AF37]/40 p-5 rounded-2xl shadow-2xl max-w-[220px]">
                <div className="text-2xl font-serif font-bold text-[#D4AF37]">
                  Seia Law
                </div>
                <div className="text-[11px] text-emerald-200/80 font-medium uppercase tracking-wider mt-1">
                  Empresa Jurídica em Moçambique
                </div>
              </div>

              {/* Decorative Geometric Accent */}
              <div className="absolute -top-4 -right-4 w-24 h-24 border-t-2 border-r-2 border-[#D4AF37]/40 rounded-tr-2xl pointer-events-none" />
            </div>
          </div>

        </div>

        {/* Bottom Section: Our Values Cards */}
        <div className="mt-20 pt-16 border-t border-emerald-900/60">
          <div className="text-center max-w-xl mx-auto mb-12">
            <h3 className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">
              Nossos Princípios Fundamentais
            </h3>
            <h4 className="text-2xl md:text-3xl font-serif font-bold text-white">
              Orientados pela Integridade e Excelência
            </h4>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((val, idx) => {
              const IconComponent = val.icon;
              return (
                <div 
                  key={idx} 
                  className="p-6 rounded-2xl bg-[#06241A]/80 border border-emerald-800/40 hover:border-[#D4AF37]/50 transition-all duration-300 group hover:-translate-y-1"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#093829] border border-emerald-700/50 flex items-center justify-center text-[#D4AF37] mb-4 group-hover:bg-[#D4AF37] group-hover:text-black transition-colors">
                    <IconComponent size={22} />
                  </div>
                  <h5 className="text-lg font-serif font-bold text-white mb-2">
                    {val.title}
                  </h5>
                  <p className="text-xs md:text-sm text-emerald-100/70 leading-relaxed font-light">
                    {val.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </section>

     <section id="services" className="py-20 md:py-32 bg-[#093829] relative overflow-hidden border-b border-emerald-900/40">
      {/* Background Mesh */}
      <div 
        className="absolute inset-0 opacity-5 pointer-events-none" 
        style={{
          backgroundImage: 'radial-gradient(#D4AF37 1px, transparent 1px)', 
          backgroundSize: '40px 40px'
        }} 
      />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="flex flex-col md:grid md:grid-cols-12 items-start md:items-end gap-6 md:gap-8 mb-14 md:mb-18">
          <div className="md:col-span-6 space-y-3">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
              <span className="w-8 h-[1px] bg-[#D4AF37]" />
              <span>Áreas de Especialização</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-white leading-tight">
              Serviços <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-200 via-white to-[#D4AF37]">
                Jurídicos
              </span>
            </h2>
          </div>

          <p className="md:col-span-4 text-emerald-100/70 text-xs md:text-sm leading-relaxed font-light md:border-l border-emerald-800/60 md:pl-6">
            Aconselhamento jurídico de alta precisão desenhado para proteger investimentos, otimizar operações empresariais e garantir conformidade regulatória em Moçambique.
          </p>

          <div className="md:col-span-2 flex justify-start md:justify-end w-full">
            <a 
              href="#contact"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black font-semibold transition-all duration-300 text-xs tracking-wider uppercase"
            >
              Consulte-Nos
            </a>
          </div>
        </div>

        {/* Bento Grid layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 md:gap-6">
          {servicesToDisplay.slice(0, visibleServices).map((item, index) => {
            // Estutura de variação bento grid de 12 colunas
            const gridSpans = [
              "sm:col-span-1 lg:col-span-4 min-h-[300px] md:h-[380px]",
              "sm:col-span-1 lg:col-span-5 min-h-[300px] md:h-[380px]",
              "sm:col-span-1 lg:col-span-3 min-h-[300px] md:h-[380px]",
              "sm:col-span-2 lg:col-span-7 min-h-[320px] md:h-[420px]",
              "sm:col-span-1 lg:col-span-5 min-h-[320px] md:h-[420px]",
              "sm:col-span-2 lg:col-span-12 min-h-[250px] md:h-[300px]",
            ];
            const currentSpan = gridSpans[index % gridSpans.length];

            const ServiceIcon = item.icon || Briefcase;

            return (
              <div
                key={item._id || index}
                onClick={() => openItem && openItem(item, 'services')}
                className={`group relative rounded-2xl overflow-hidden cursor-pointer bg-[#06241A] border border-emerald-800/40 hover:border-[#D4AF37]/60 transition-all duration-500 shadow-xl ${currentSpan}`}
              >
                {/* Background Image with Dark Gradient Overlay */}
                <div className="absolute inset-0 z-0">
                  {item.images?.length > 0 ? (
                    <img 
                      src={getImageUrl ? getImageUrl(item.images[0]) : item.images[0]} 
                      alt={item.name}
                      className="w-full h-full object-cover filter brightness-50 contrast-110 transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-[#06241A]" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#06241A] via-[#06241A]/70 to-transparent" />
                </div>

                {/* Top Action & Badge */}
                <div className="absolute top-4 md:top-6 left-4 md:left-6 right-4 md:right-6 z-20 flex items-center justify-between">
                  {item.badge ? (
                    <span className="bg-[#093829]/90 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] uppercase font-bold px-3 py-1 rounded-full backdrop-blur-md">
                      {item.badge}
                    </span>
                  ) : (
                    <span className="w-2" />
                  )}

                  <div className="w-9 h-9 md:w-10 md:h-10 bg-[#093829]/90 border border-emerald-700/40 rounded-full flex items-center justify-center text-[#D4AF37] shadow-lg group-hover:bg-[#D4AF37] group-hover:text-black transition-all duration-300">
                    <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </div>

                {/* Bottom Content Card */}
                <div className="absolute bottom-4 md:bottom-6 left-4 md:left-6 right-4 md:right-6 z-20">
                  <div className="bg-[#06241A]/85 backdrop-blur-md p-5 md:p-6 rounded-xl border border-emerald-800/50 group-hover:border-[#D4AF37]/30 transition-colors">
                    <div className="flex items-center gap-3 mb-2">
                      <ServiceIcon size={18} className="text-[#D4AF37] shrink-0" />
                      <h3 className="text-base md:text-xl font-serif font-bold text-white group-hover:text-[#D4AF37] transition-colors">
                        {item.name}
                      </h3>
                    </div>
                    
                    <p className="text-emerald-100/70 text-xs md:text-sm font-light line-clamp-2 leading-relaxed">
                      {item.description || "Aconselhamento especializado e soluções estratégicas de alto padrão."}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Expand Services Button */}
        {visibleServices < servicesToDisplay.length && setVisibleServices && (
          <div className="mt-12 md:mt-16 flex justify-center">
            <button 
              onClick={() => setVisibleServices(prev => prev + 3)}
              className="group flex items-center gap-3 px-8 py-4 bg-[#D4AF37] hover:bg-[#C5A059] text-black font-bold rounded-full transition-all shadow-xl text-xs uppercase tracking-wider"
            >
              <span>Mais Áreas de Prática</span>
              <Plus size={16} className="group-hover:rotate-90 transition-transform" />
            </button>
          </div>
        )}

      </div>
    </section>

      <section className="py-20 md:py-32 bg-[#06241A] px-4 md:px-8 relative overflow-hidden border-b border-emerald-900/40">
      {/* Background Subtle Lines */}
      <div 
        className="absolute inset-0 opacity-5 pointer-events-none" 
        style={{
          backgroundImage: 'radial-gradient(#D4AF37 1px, transparent 1px)', 
          backgroundSize: '36px 36px'
        }} 
      />

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20 space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#093829] border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-semibold uppercase tracking-widest">
            <span>Nossa base</span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-white tracking-tight leading-tight">
            {portalContent?.missionVision?.title || (
              <>
                 Uma Abordagem <br className="hidden sm:block" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-200 via-white to-[#D4AF37]">
                  Estratégica
                </span>
              </>
            )}
          </h2>

          <p className="text-emerald-100/70 text-sm md:text-base font-light max-w-xl mx-auto">
            Combinação única de inteligência jurídica local, padrões internacionais e tecnologia avançada para investidores e instituições.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 relative z-10">
          
          {/* Card 1: Mission */}
          <div className="bg-[#093829] border border-emerald-800/40 hover:border-[#D4AF37]/50 p-8 rounded-2xl transition-all duration-300 hover:-translate-y-1 group flex flex-col justify-between shadow-xl">
            <div>
              <div className="w-12 h-12 rounded-xl bg-[#06241A] border border-emerald-700/40 flex items-center justify-center mb-6 text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-black transition-colors">
                <Target size={22} />
              </div>

              <h3 className="text-xl font-serif font-bold text-white mb-3 flex items-center justify-between">
                <span>{portalContent?.missionVision?.mission?.title || "Our Mission"}</span>
                <span className="text-[10px] text-[#D4AF37] tracking-widest uppercase font-mono">01</span>
              </h3>

              <p className="text-emerald-100/70 text-xs md:text-sm leading-relaxed font-light mb-6">
                {portalContent?.missionVision?.mission?.content || 
                  "Oferecer advocacia de excelência e soluções jurídicas estratégicas, atuando como a ponte segura para investidores nacionais e internacionais em Moçambique."}
              </p>
            </div>

            <a href="#services" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#D4AF37] hover:text-white transition-colors pt-4 border-t border-emerald-900/60">
              <span>Explorar Serviços</span>
              <ArrowUpRight size={14} />
            </a>
          </div>

          {/* Card 2: Vision */}
          <div className="bg-[#093829] border border-emerald-800/40 hover:border-[#D4AF37]/50 p-8 rounded-2xl transition-all duration-300 hover:-translate-y-1 group flex flex-col justify-between shadow-xl">
            <div>
              <div className="w-12 h-12 rounded-xl bg-[#06241A] border border-emerald-700/40 flex items-center justify-center mb-6 text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-black transition-colors">
                <Eye size={22} />
              </div>

              <h3 className="text-xl font-serif font-bold text-white mb-3 flex items-center justify-between">
                <span>{portalContent?.missionVision?.vision?.title || "Our Vision"}</span>
                <span className="text-[10px] text-[#D4AF37] tracking-widest uppercase font-mono">02</span>
              </h3>

              <p className="text-emerald-100/70 text-xs md:text-sm leading-relaxed font-light mb-6">
                {portalContent?.missionVision?.vision?.content || 
                  "Ser a referência moçambicana de advocacia de nova geração no mercado global, reconhecida pela precisão técnica, visão corporativa e tecnologia jurídica."}
              </p>
            </div>

            <a href="#about" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#D4AF37] hover:text-white transition-colors pt-4 border-t border-emerald-900/60">
              <span>Nossa História</span>
              <ArrowUpRight size={14} />
            </a>
          </div>

          {/* Card 3: Values */}
          <div className="bg-[#093829] border border-emerald-800/40 hover:border-[#D4AF37]/50 p-8 rounded-2xl transition-all duration-300 hover:-translate-y-1 group flex flex-col justify-between shadow-xl">
            <div>
              <div className="w-12 h-12 rounded-xl bg-[#06241A] border border-emerald-700/40 flex items-center justify-center mb-6 text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-black transition-colors">
                <Shield size={22} />
              </div>

              <h3 className="text-xl font-serif font-bold text-white mb-3 flex items-center justify-between">
                <span>{portalContent?.missionVision?.values?.title || "Core Values"}</span>
                <span className="text-[10px] text-[#D4AF37] tracking-widest uppercase font-mono">03</span>
              </h3>

              <div className="space-y-3 mb-6">
                {defaultValues.map((val, idx) => (
                  <div key={idx} className="flex items-start gap-2.5">
                    <CheckCircle2 size={14} className="text-[#D4AF37] shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs font-bold text-white">{val.name}</div>
                      <div className="text-[11px] text-emerald-200/60 font-light">{val.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <a href="#contact" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#D4AF37] hover:text-white transition-colors pt-4 border-t border-emerald-900/60">
              <span>Trabalhe Conosco</span>
              <ArrowUpRight size={14} />
            </a>
          </div>

        </div>

        {/* Central Call to Action Banner */}
        <div className="mt-16 text-center">
          <a
            href="#contact"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black font-bold text-sm hover:brightness-110 transition-all shadow-xl shadow-black/30 group"
          >
            <span>Investe em Moçambique com a SEIA LAW</span>
            <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        </div>

      </div>
    </section>

    <section className="py-20 md:py-32 bg-[#06241A] text-white relative overflow-hidden border-b border-emerald-900/40">
      {/* Dynamic Glow Effect */}
      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        
        {/* Header Block */}
        <div className="mb-14 md:mb-18 flex flex-col lg:grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7 space-y-3">
            <div className="flex items-center gap-3">
              <span className="w-8 h-[1px] bg-[#D4AF37]" />
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37]">
                Conhecimento & Perceções
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold leading-tight">
              Recursos Estratégicos  <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-200 via-white to-[#D4AF37]">
               para Investidores
              </span>
            </h2>
          </div>

          <div className="lg:col-span-5 flex flex-col lg:items-end gap-5">
            <p className="text-emerald-100/70 text-xs md:text-sm font-light max-w-md lg:text-right leading-relaxed">
              Aceda aos nossos guias jurídicos, relatórios regulatórios e publicações estratégicas concebidas para apoiar tomadas de decisão seguras em Moçambique.
            </p>
            
            {/* Control Arrows for Slider or Quick Navigation */}
            <div className="flex gap-3">
              <button 
                aria-label="Previous resource"
                className="w-10 h-10 rounded-full border border-emerald-800/60 flex items-center justify-center text-emerald-300 hover:bg-[#093829] hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition-all cursor-pointer"
              >
                <ChevronLeft size={18} />
              </button>
              <button 
                aria-label="Next resource"
                className="w-10 h-10 rounded-full bg-[#093829] border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] shadow-lg hover:bg-[#D4AF37] hover:text-black transition-all cursor-pointer"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Resource Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {(showAllProducts ? itemsToDisplay : itemsToDisplay.slice(0, 4)).map((item, index) => {
            const firstImage = Array.isArray(item.images) && item.images.length > 0 ? item.images[0] : item.image;
            const imageUrl = getImageUrl ? getImageUrl(firstImage || '') : firstImage;
            const isFeatured = index === 0 || item.isFeatured;
            const price = getItemPrice ? getItemPrice(item) : 0;

            return (
              <div
                key={item._id || index}
                onClick={() => openItem && openItem(item, 'products')}
                className={`group relative flex flex-col transition-all duration-500 cursor-pointer rounded-2xl overflow-hidden border shadow-xl ${
                  isFeatured 
                    ? 'bg-[#093829] border-[#D4AF37]/60 hover:border-[#D4AF37]' 
                    : 'bg-[#06241A] border-emerald-900/60 hover:border-emerald-700/60'
                }`}
              >
                {/* Content Details */}
                <div className="p-6 md:p-7 pb-4">
                  <div className="flex justify-between items-start mb-5">
                    <span className={`px-3 py-1 rounded-full text-[10px] font-mono tracking-wider uppercase border ${
                      isFeatured 
                        ? 'border-[#D4AF37]/40 text-[#D4AF37] bg-[#06241A]/60' 
                        : 'border-emerald-800/60 text-emerald-300/80 bg-[#093829]/40'
                    }`}>
                      {item.category || 'Legal Insight'}
                    </span>

                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-transform group-hover:scale-110 ${
                      isFeatured ? 'bg-[#D4AF37] text-black' : 'bg-[#093829] text-[#D4AF37] border border-emerald-800'
                    }`}>
                      <ArrowUpRight size={15} />
                    </div>
                  </div>

                  <h3 className="text-lg font-serif font-bold mb-2.5 leading-snug group-hover:text-[#D4AF37] transition-colors">
                    {item.name}
                  </h3>

                  <p className="text-emerald-100/60 text-xs font-light leading-relaxed line-clamp-3 mb-4">
                    {item.description || "Análise jurídica estratégica e perspetivas regulatórias atualizadas."}
                  </p>

                  <div className="flex items-center justify-between text-xs font-mono pt-2 border-t border-emerald-900/40 text-emerald-200/80">
                    {price > 0 ? (
                      <span className="text-[#D4AF37] font-bold">
                        {price.toLocaleString()} {currency}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 text-[#D4AF37] font-medium">
                        <Download size={13} />
                        <span>Acesso Institucional</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Cover Image Block */}
                <div className="mt-auto p-4 pt-0">
                  <div className="h-44 md:h-52 w-full overflow-hidden rounded-xl relative">
                    {imageUrl ? (
                      <img 
                        src={imageUrl} 
                        alt={item.name} 
                        className="w-full h-full object-cover filter brightness-75 group-hover:brightness-90 transition-all duration-700 group-hover:scale-105" 
                      />
                    ) : (
                      <div className="w-full h-full bg-[#093829] flex items-center justify-center text-emerald-600">
                        <BookOpen size={32} />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#06241A]/90 via-transparent to-transparent" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* View All Button */}
        {setShowAllProducts && (
          <div className="mt-16 flex justify-center">
            <button 
              onClick={() => setShowAllProducts(prev => !prev)}
              className="group flex items-center gap-3 px-8 py-4 bg-[#093829] hover:bg-[#0d4a37] border border-[#D4AF37]/40 text-[#D4AF37] hover:text-white text-xs font-bold uppercase tracking-widest rounded-full shadow-2xl transition-all"
            >
              <span>{showAllProducts ? 'Show Less Insights' : 'Access Client Portal & Insights'}</span>
              <div className="w-6 h-6 rounded-full bg-[#D4AF37] text-black flex items-center justify-center transition-transform group-hover:translate-x-1">
                <ChevronRight size={14} />
              </div>
            </button>
          </div>
        )}

      </div>
    </section>

    <section className="py-20 md:py-32 bg-[#093829] text-white relative overflow-hidden border-b border-emerald-900/40">
      {/* Background Ambient Effect */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#D4AF37]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="mb-12 md:mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl space-y-3">
            <div className="flex items-center gap-3">
              <span className="w-8 h-[1px] bg-[#D4AF37]" />
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37]">
                Retentores e Soluções Estratégicas
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold leading-tight">
              Pacotes Integrados de  <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-200 via-white to-[#D4AF37]">
                Serviços Jurídicos
              </span>
            </h2>
          </div>

          <p className="text-emerald-100/70 text-xs md:text-sm font-light max-w-xs md:text-right leading-relaxed">
            Estruturas de assessoria contínua e programas integrados desenhados para mitigar riscos e impulsionar o crescimento do seu negócio.
          </p>
        </div>

        {/* Horizontal Scroll / Carousel Container */}
        <div className="flex gap-6 overflow-x-auto pb-8 pt-2 scrollbar-hide snap-x">
          {displayBundles.map((item, index) => {
            const image = getImageUrl ? getImageUrl(item.image || '') : item.image;
            const price = getItemPrice ? getItemPrice(item) : (item.price || 0);

            return (
              <div
                key={item._id || index}
                onClick={() => openItem && openItem(item, 'bundles')}
                className="group relative min-w-[85vw] sm:min-w-[480px] md:min-w-[580px] lg:min-w-[750px] snap-center transition-all duration-500 cursor-pointer"
              >
                <div className="bg-[#06241A] rounded-2xl p-6 md:p-8 flex flex-col md:flex-row gap-6 md:gap-8 h-full border border-emerald-800/50 group-hover:border-[#D4AF37]/60 shadow-2xl transition-all">
                  
                  {/* Left Column Image */}
                  <div className="w-full md:w-[280px] lg:w-[320px] h-[220px] md:h-auto rounded-xl overflow-hidden relative shrink-0">
                    <img 
                      src={image} 
                      alt={item.name} 
                      className="w-full h-full object-cover filter brightness-75 contrast-110 transition-transform duration-700 group-hover:scale-105" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#06241A] via-transparent to-transparent md:hidden" />
                    
                    <div className="absolute top-4 left-4 bg-[#093829]/90 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] font-mono font-bold px-3 py-1 rounded-full uppercase tracking-wider backdrop-blur-md">
                      Conjunto Estratégico
                    </div>
                  </div>

                  {/* Right Column Content */}
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-start mb-3">
                        <span className="text-[10px] font-mono font-bold text-[#D4AF37] uppercase tracking-widest">
                          Pacote de Soluções {index + 1 < 10 ? `0${index + 1}` : index + 1}
                        </span>
                        
                        <div className="w-9 h-9 rounded-full bg-[#093829] border border-emerald-700/40 flex items-center justify-center text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-black transition-all">
                          <ArrowUpRight size={16} />
                        </div>
                      </div>

                      <h3 className="text-xl md:text-2xl lg:text-3xl font-serif font-bold text-white mb-3 leading-snug group-hover:text-[#D4AF37] transition-colors">
                        {item.name}
                      </h3>

                      <p className="text-emerald-100/70 text-xs md:text-sm font-light leading-relaxed mb-5 line-clamp-2">
                        {item.description || "Aconselhamento abrangente e suporte jurídico contínuo."}
                      </p>

                      {/* Bullet Features list if available */}
                      {item.features && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6 pt-4 border-t border-emerald-900/40">
                          {item.features.map((feat, fIdx) => (
                            <div key={fIdx} className="flex items-center gap-2 text-[11px] text-emerald-200/80 font-light">
                              <CheckCircle2 size={12} className="text-[#D4AF37] shrink-0" />
                              <span className="truncate">{feat}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Bottom Investment & CTA */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between border-t border-emerald-900/60 pt-5 gap-4">
                      <div>
                        <span className="text-[9px] font-mono text-emerald-300/60 uppercase tracking-widest block">
                          Investimento / Retentor
                        </span>
                        <div className="text-xl md:text-2xl font-serif font-bold text-white">
                          {price > 0 ? (
                            <>
                              {price.toLocaleString()} <span className="text-xs font-mono text-[#D4AF37]">{currency}</span>
                            </>
                          ) : (
                            <span className="text-sm text-[#D4AF37] font-sans font-medium">Custom Proposal</span>
                          )}
                        </div>
                      </div>

                      <button className="bg-[#D4AF37] hover:bg-[#C5A059] text-black px-6 py-3 rounded-full text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-all">
                        <span>Selecionar Plano</span>
                        <Layers size={14} />
                      </button>
                    </div>

                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Scroll Indicator Footer */}
        <div className="flex flex-col items-center mt-8 gap-3">
          <div className="w-32 md:w-48 h-[2px] bg-emerald-900/60 rounded-full overflow-hidden">
            <div className="bg-[#D4AF37] h-full w-1/3 rounded-full" />
          </div>
          <span className="text-[9px] font-mono text-[#D4AF37] uppercase tracking-[0.3em]">
            deslize para explorar opções
          </span>
        </div>

      </div>
    </section>

     <section className="py-20 md:py-32 bg-[#06241A] text-white relative overflow-hidden border-b border-emerald-900/40">
      {/* Background Subtle Gradient */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-[#D4AF37]/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        
        {/* Header Block */}
        <div className="flex flex-col items-center text-center mb-14 md:mb-20 max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-[1px] bg-[#D4AF37]" />
            <span className="text-xs uppercase tracking-[0.25em] font-mono text-[#D4AF37]">
              Plano de Aconselhamento Jurídico
            </span>
            <span className="w-8 h-[1px] bg-[#D4AF37]" />
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold leading-tight mb-4">
            Retentores <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-200 via-white to-[#D4AF37]">Contínuos</span>
          </h2>

          <p className="text-emerald-100/70 text-xs md:text-sm font-light max-w-lg leading-relaxed mb-8">
            Ganta segurança jurídica e suporte regulatório contínuo com planos de avença ajustados à escala do seu investimento em Moçambique.
          </p>

          {/* Billing Toggle (Monthly / Yearly) */}
          <div className="inline-flex items-center bg-[#093829] p-1.5 rounded-full border border-emerald-800/60 shadow-inner">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-5 py-2 rounded-full text-xs font-mono transition-all duration-300 ${
                billingCycle === 'monthly'
                  ? 'bg-[#D4AF37] text-black font-bold shadow-md'
                  : 'text-emerald-200/70 hover:text-white'
              }`}
            >
              Retentor Mensal 
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-5 py-2 rounded-full text-xs font-mono transition-all duration-300 flex items-center gap-2 ${
                billingCycle === 'yearly'
                  ? 'bg-[#D4AF37] text-black font-bold shadow-md'
                  : 'text-emerald-200/70 hover:text-white'
              }`}
            >
              <span>Retentor Anual</span>
              <span className="bg-emerald-900/90 text-[#D4AF37] text-[9px] px-2 py-0.5 rounded-full font-sans border border-[#D4AF37]/30">
                poupe 11%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
          {displayPlans.map((plan, index) => {
            const isPopular = plan.popular || index === 1;
            
            // Cálculo do preço dinâmico com suporte para o toggle
            const basePrice = getItemPrice ? getItemPrice(plan) : 0;
            const computedPrice = basePrice > 0 
              ? (billingCycle === 'yearly' ? basePrice * 10 : basePrice)
              : (billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice);

            return (
              <div
                key={plan._id || index}
                className={`relative p-8 md:p-10 rounded-2xl flex flex-col justify-between transition-all duration-500 border ${
                  isPopular
                    ? 'bg-[#093829] border-[#D4AF37] shadow-2xl md:-translate-y-2'
                    : 'bg-[#06241A] border-emerald-900/80 hover:border-emerald-700/60'
                }`}
              >
                {/* Popular Badge */}
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-black text-[10px] font-mono font-bold uppercase tracking-widest px-4 py-1 rounded-full shadow-lg flex items-center gap-1.5">
                    <Crown size={12} />
                    <span>Mais populares</span>
                  </div>
                )}

                {/* Card Top Information */}
                <div>
                  <div className="mb-6">
                    <h3 className="text-2xl font-serif font-bold text-white mb-2 flex items-center justify-between">
                      <span>{plan.name}</span>
                    </h3>
                    <p className="text-emerald-100/60 text-xs font-light leading-relaxed min-h-[3rem]">
                      {plan.description || "Aconselhamento corporativo preventivo e estratégico."}
                    </p>
                  </div>

                  {/* Price Tag */}
                  <div className="mb-8 p-4 rounded-xl bg-[#06241A]/60 border border-emerald-900/50 flex items-baseline gap-2">
                    <span className="text-3xl md:text-4xl font-serif font-bold text-white">
                      {computedPrice ? computedPrice.toLocaleString() : 'Custom'}
                    </span>
                    <span className="text-xs font-mono text-[#D4AF37]">
                      {currency} / {billingCycle === 'yearly' ? 'yr' : 'mo'}
                    </span>
                  </div>

                  {/* Included Features List */}
                  <div className="mb-8">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-[#D4AF37] mb-4">
                      Escopo de Serviços
                    </p>
                    <ul className="space-y-3.5">
                      {(plan.includedLimits || []).map((limit, i) => (
                        <li key={i} className="flex items-start gap-3 text-xs text-emerald-100/80 font-light">
                          <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                            isPopular ? 'bg-[#D4AF37] text-black' : 'bg-emerald-900/80 text-[#D4AF37]'
                          }`}>
                            <Check size={10} strokeWidth={3} />
                          </div>
                          <span className="leading-tight">{limit.description || limit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Action Button */}
                <button
                  onClick={() => addToCart && addToCart(plan, 'bundles')}
                  className={`w-full py-4 rounded-full font-bold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 group ${
                    isPopular
                      ? 'bg-[#D4AF37] text-black hover:bg-[#C5A059]'
                      : 'bg-[#093829] text-white hover:bg-emerald-800 border border-emerald-700/50'
                  }`}
                >
                  <span>Aderir pacote</span>
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </button>

              </div>
            );
          })}
        </div>

        {/* Footnote Guarantee */}
        <div className="mt-16 flex items-center justify-center gap-3 text-xs text-emerald-200/60 font-light text-center">
          <ShieldCheck size={18} className="text-[#D4AF37] shrink-0" />
          <span>Todos os planos incluem acordo de confidencialidade (NDA) e SLA de resposta garantido em contrato.</span>
        </div>

      </div>
    </section>
      {/* CLIENTS / PARTNERS */}
      {portalContent?.clients?.enabled && portalContent.clients.items?.length > 0 && (
        <section className="py-16 md:py-24 bg-[#f8f7f2] relative overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-[1px] bg-gradient-to-r from-transparent via-[#0E7D83]/10 to-transparent" />

          <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
            <div className="flex flex-col items-center mb-12 md:mb-16">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-6 md:w-8 h-[1px] bg-[#0E7D83] opacity-20" />
                <span className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.5em] text-[#0E7D83] opacity-60">
                  Trusted by Industry Leaders
                </span>
                <div className="w-6 md:w-8 h-[1px] bg-[#0E7D83] opacity-20" />
              </div>
            </div>

            <div className="flex flex-wrap justify-center items-center gap-x-12 md:gap-x-16 gap-y-8 md:gap-y-12">
              {portalContent.clients.items.map((client: any, i: number) => (
                <div 
                  key={i} 
                  className="group relative flex items-center justify-center transition-all duration-500"
                >
                  <div className="absolute inset-0 bg-white blur-2xl rounded-full opacity-0 group-hover:opacity-40 transition-opacity duration-700" />
                  <img 
                    src={getImageUrl(client.logo)} 
                    alt={client.name} 
                    className="h-6 md:h-10 w-auto object-contain grayscale opacity-40 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500 relative z-10" 
                  />
                </div>
              ))}
            </div>

            <div className="mt-12 md:mt-16 flex justify-center">
              <div className="h-1.5 w-1.5 rounded-full bg-[#E5E65D]" />
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {portalContent?.testimonials?.enabled && portalContent.testimonials.items?.length > 0 && (
        <section id="testimonials" className="py-16 md:py-24 bg-white relative overflow-hidden">
          <div className="absolute -top-24 -right-24 opacity-[0.03] rotate-12 pointer-events-none">
            <Quote size={300} className="text-[#0E7D83]" />
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
            <div className="flex flex-col items-center text-center mb-16 md:mb-20">
              <span className="bg-[#E5E65D] text-[#0E7D83] text-[9px] md:text-[10px] font-black uppercase tracking-[0.4em] px-4 md:px-5 py-1.5 rounded-full mb-4 md:mb-6">
                Testimonials
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0E7D83] tracking-tight leading-none">
                Trusted by <span className="text-[#0a5e63]">Industry Leaders</span>
              </h2>
              <div className="mt-4 md:mt-6 w-10 md:w-12 h-1 bg-[#0E7D83] rounded-full opacity-10" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {portalContent.testimonials.items.map((t, i) => {
                const isFeatured = i === 1;

                return (
                  <div 
                    key={i} 
                    className={`p-8 md:p-10 flex flex-col justify-between transition-all duration-500 hover:shadow-xl mobile-clip-none
                      ${isFeatured 
                        ? 'bg-[#0E7D83] text-white' 
                        : 'bg-[#f8f7f2] text-[#0E7D83]'
                      }`}
                    style={{
                      clipPath: 'polygon(0 0, 85% 0, 100% 10%, 100% 100%, 0 100%)',
                      borderRadius: '2rem'
                    }}
                  >
                    <div>
                      <div className="flex gap-1 mb-6 md:mb-8">
                        {Array.from({ length: t.rating || 5 }).map((_, k) => (
                          <Star key={k} size={12} className="fill-[#E5E65D] text-[#E5E65D]" />
                        ))}
                      </div>

                      <blockquote className="relative">
                        <p className={`text-base md:text-lg font-bold leading-snug tracking-tight mb-8 md:mb-10 ${isFeatured ? 'text-white' : 'text-[#0E7D83]'}`}>
                          "{t.text}"
                        </p>
                      </blockquote>
                    </div>

                    <div className="flex items-center gap-3 md:gap-4 pt-6 md:pt-8 border-t border-current opacity-20">
                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 overflow-hidden shrink-0 grayscale group-hover:grayscale-0 transition-all border border-current opacity-40">
                        {t.avatar ? (
                          <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className={`w-full h-full flex items-center justify-center font-bold text-[10px] uppercase ${isFeatured ? 'bg-white text-[#0E7D83]' : 'bg-[#0E7D83] text-white'}`}>
                            {t.name?.substring(0, 2)}
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="font-black text-[11px] md:text-sm uppercase tracking-tighter leading-none mb-1">{t.name}</p>
                        <p className={`text-[8px] md:text-[9px] font-bold uppercase tracking-widest opacity-60`}>
                          {t.role} <span className="mx-1">/</span> {t.company}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-16 md:mt-20 flex flex-col items-center gap-3 md:gap-4">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-[#E5E65D]" />
                <span className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.5em] text-[#0E7D83] opacity-30">
                  Verified Success Stories
                </span>
                <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-[#E5E65D]" />
              </div>
            </div>
          </div>
        </section>
      )}

     {/* ITEM DETAILS MODAL */}
{selectedItem && (
  <div className="fixed inset-0 z-[100] flex justify-end">
    {/* Backdrop */}
    <div 
      className="absolute inset-0 bg-[#06241A]/70 backdrop-blur-md transition-opacity duration-500" 
      onClick={() => { setSelectedItem(null); setCurrentImgIndex(0); }} 
    />

    {/* Drawer Container */}
    <div className="relative w-full max-w-full lg:max-w-6xl bg-[#06241A] text-white shadow-[-20px_0_80px_rgba(0,0,0,0.5)] h-full overflow-y-auto animate-in slide-in-from-right duration-500 border-l border-emerald-900/40">
      
      {/* Close Button */}
      <button 
        onClick={() => { setSelectedItem(null); setCurrentImgIndex(0); }} 
        className="fixed top-4 right-4 md:top-8 md:right-8 z-[110] w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-[#093829] text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37] hover:text-black transition-all duration-300 shadow-2xl cursor-pointer"
        aria-label="Close modal"
      >
        <X size={20} strokeWidth={2.5} />
      </button>

      <div className="flex flex-col lg:flex-row lg:min-h-screen">
        
        {/* Left Gallery Section */}
        <div className="relative w-full lg:w-[55%] lg:h-screen bg-[#093829] p-6 md:p-8 lg:p-12 flex flex-col justify-between border-b lg:border-b-0 lg:border-r border-emerald-900/60">
          <div className="flex items-center gap-3 mb-6 md:mb-8">
            <span className="w-8 h-[1px] bg-[#D4AF37]" />
            <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#D4AF37]">
              Visão geral
            </span>
          </div>

          <div className="flex-1 relative group flex items-center justify-center my-auto">
            {(() => {
              const itemImages = selectedItem.images && Array.isArray(selectedItem.images) && selectedItem.images.length > 0
                ? selectedItem.images 
                : selectedItem.image ? [selectedItem.image] : [];
              const imageUrl = getImageUrl(itemImages[currentImgIndex] || '');
              
              return (
                <div className="w-full h-[320px] md:h-[450px] lg:h-[80%] relative">
                  <div className="w-full h-full rounded-2xl overflow-hidden border border-emerald-800/60 shadow-2xl bg-[#06241A] relative">
                    {imageUrl ? (
                      <img src={imageUrl} className="w-full h-full object-cover filter brightness-90 contrast-105" alt={selectedItem.name} />
                    ) : (
                      <div className="w-full h-full bg-[#06241A] flex flex-col items-center justify-center text-emerald-700 gap-3">
                        <Package size={50} />
                        <span className="text-xs font-mono text-emerald-500/60 uppercase tracking-widest">SEIA LAW Brief</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#06241A]/80 via-transparent to-transparent pointer-events-none" />
                  </div>

                  {/* Thumbnail Indicators */}
                  {itemImages.length > 1 && (
                    <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 flex gap-2 md:gap-3 bg-[#06241A]/90 backdrop-blur-md px-4 py-2 rounded-full border border-emerald-800/80 shadow-xl">
                      {itemImages.map((_, idx) => (
                        <button 
                          key={idx}
                          onClick={() => setCurrentImgIndex(idx)}
                          className={`h-1.5 rounded-full transition-all duration-300 ${idx === currentImgIndex ? 'w-8 bg-[#D4AF37]' : 'w-2 bg-emerald-800'}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          <div className="hidden lg:flex items-center gap-2 text-[11px] font-mono text-emerald-300/50 pt-6">
            <span>SEIA LAW</span>
            <span>•</span>
            <span>Confidential & Proprietary</span>
          </div>
        </div>

        {/* Right Details Section */}
        <div className="flex-1 p-6 md:p-10 lg:p-16 flex flex-col justify-between bg-[#06241A]">
          <div>
            {/* Header Badge & Rating */}
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <span className="text-[10px] font-mono font-bold text-[#D4AF37] uppercase tracking-widest px-3.5 py-1.5 bg-[#093829] border border-[#D4AF37]/30 rounded-full">
                {selectedItem.category || 'Strategic Practice'}
              </span>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(s => (
                  <Star key={s} size={11} className="fill-[#D4AF37] text-[#D4AF37]" />
                ))}
              </div>
            </div>

            {/* Title */}
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-white mb-6 leading-tight">
              {selectedItem.name}
            </h3>

            {/* Description */}
            <p className="text-emerald-100/70 text-xs md:text-sm lg:text-base leading-relaxed mb-8 md:mb-10 font-light">
              {selectedItem.description || "Aconselhamento jurídico de excelência desenhado para responder às exigências regulatórias e estratégicas do mercado moçambicano."}
            </p>

            {/* Core Provisions / Items List */}
            <div className="space-y-3 mb-8 md:mb-12">
              <h4 className="text-[#D4AF37] text-[10px] font-mono font-bold uppercase tracking-[0.25em] mb-4">
               Principais Disposições e Entregáveis
              </h4>
              {(selectedItem.includedItems || selectedItem.items || []).slice(0, 5).map((it, i) => (
                <div key={i} className="flex items-center gap-3.5 py-3 border-b border-emerald-900/50 group">
                  <div className="w-5 h-5 rounded-full bg-[#093829] border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] shrink-0">
                    <Check size={10} strokeWidth={3} />
                  </div>
                  <span className="text-xs md:text-sm font-light text-emerald-100/90">
                    {it.productId?.name || it.description || "Aconselhamento Regulatório Especializado"}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing & CTA Section */}
          <div className="pt-6 md:pt-8 border-t border-emerald-900/60 mt-auto">
            <div className="flex flex-col gap-6">
              <div className="flex justify-between items-end">
                <div>
                  <span className="text-[10px] font-mono text-emerald-300/60 uppercase tracking-widest block mb-1">
                   Investimento em Serviços
                  </span>
                  <div className="text-3xl md:text-4xl font-serif font-bold text-white tracking-tight">
                    {getItemPrice(selectedItem).toLocaleString()} <span className="text-sm font-mono text-[#D4AF37] font-normal">{currency}</span>
                  </div>
                </div>
                {selectedItem.billingCycle && (
                  <span className="text-[10px] font-mono text-[#D4AF37] uppercase bg-[#093829] px-3 py-1 rounded-full border border-emerald-800">
                    Faturado {selectedItem.billingCycle}
                  </span>
                )}
              </div>

              <button 
                onClick={() => { addToCart(selectedItem, activeCatalog); setSelectedItem(null); }}
                className="w-full py-4 md:py-5 bg-[#D4AF37] hover:bg-[#C5A059] text-black font-bold text-xs uppercase tracking-[0.25em] rounded-full shadow-2xl transition-all transform active:scale-[0.98] flex items-center justify-center gap-3 cursor-pointer"
              >
                <span>Solicitar Requisição Formal</span>
                <ArrowRight size={16} />
              </button>
            </div>
          </div>

        </div>

      </div>
    </div>
  </div>
)}

     {/* CART MODAL */}
<div className={`fixed inset-0 z-[100] flex justify-end ${isCartOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}>
  {/* Backdrop */}
  <div
    className={`absolute inset-0 bg-[#06241A]/70 backdrop-blur-md transition-opacity duration-700 ${isCartOpen ? 'opacity-100' : 'opacity-0'}`}
    onClick={() => setIsCartOpen(false)}
  />

  {/* Drawer Body */}
  <div
    className={`relative w-full max-w-full lg:max-w-6xl bg-[#06241A] text-white shadow-[-20px_0_80px_rgba(0,0,0,0.5)] h-full transform transition-all duration-700 ease-out flex flex-col overflow-y-auto lg:overflow-hidden ${isCartOpen ? 'translate-x-0' : 'translate-x-full'}`}
  >
    {/* Close Button */}
    <button 
      onClick={() => setIsCartOpen(false)} 
      className="absolute top-4 right-4 md:top-8 md:right-8 z-[110] w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-[#093829] text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37] hover:text-black transition-all duration-300 shadow-2xl cursor-pointer"
      aria-label="Close cart"
    >
      <X size={20} strokeWidth={2.5} />
    </button>

    <div className="flex flex-col lg:flex-row lg:h-full lg:overflow-hidden">
      
      {/* LEFT COLUMN: Selected Services List */}
      <div className="lg:flex-[1.3] lg:flex lg:flex-col bg-[#093829] border-b lg:border-b-0 lg:border-r border-emerald-900/60 z-10">
        
        {/* Header */}
        <div className="p-6 md:p-10 lg:p-12 border-b border-emerald-800/40">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-8 h-[1px] bg-[#D4AF37]" />
            <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#D4AF37]">
              Serviços Selecionados
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif font-bold text-white tracking-tight uppercase">
            Itens <span className="text-[#D4AF37] italic font-serif lowercase">Selecionados</span>
          </h2>
        </div>

        {/* Item List */}
        <div className="lg:flex-1 lg:overflow-y-auto p-6 md:p-8 lg:p-12 space-y-4">
          {cart.length > 0 ? (
            <div className="space-y-4">
              {cart.map(cartItem => (
                <div 
                  key={cartItem.itemId} 
                  className="group relative flex items-center gap-4 p-4 md:p-5 bg-[#06241A] border border-emerald-800/60 rounded-2xl hover:border-[#D4AF37]/40 transition-all duration-300"
                >
                  {/* Thumbnail */}
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-[#093829] flex items-center justify-center p-2 border border-emerald-800/40 shrink-0 overflow-hidden">
                    <img 
                      src={getImageUrl(cartItem.image)} 
                      alt="" 
                      className="w-full h-full object-cover rounded-lg filter brightness-90 group-hover:brightness-100 transition-all duration-500" 
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-[9px] font-mono font-bold text-[#D4AF37] uppercase tracking-wider mb-1">
                      {cartItem.category || 'Strategic Practice'}
                    </p>
                    <h4 className="text-sm md:text-base font-serif font-bold text-white truncate">
                      {cartItem.name}
                    </h4>
                    <button 
                      onClick={() => removeFromCart(cartItem.itemId)} 
                      className="text-[9px] text-red-400/80 hover:text-red-400 transition-colors mt-2 uppercase font-mono tracking-wider flex items-center gap-1 cursor-pointer"
                    >
                      <X size={10} /> Remover
                    </button>
                  </div>

                  {/* Quantity & Price */}
                  <div className="flex flex-col items-end gap-3 shrink-0">
                    <div className="flex items-center bg-[#093829] rounded-full p-1 border border-emerald-800/60">
                      <button 
                        onClick={() => updateCartQuantity(cartItem.itemId, cartItem.quantity - 1)} 
                        className="p-1 md:p-1.5 text-emerald-200 hover:text-white hover:bg-emerald-800/50 rounded-full cursor-pointer"
                      >
                        <Minus size={10} />
                      </button>
                      <span className="px-2 md:px-3 text-xs font-mono font-bold text-[#D4AF37]">
                        {cartItem.quantity}
                      </span>
                      <button 
                        onClick={() => updateCartQuantity(cartItem.itemId, cartItem.quantity + 1)} 
                        className="p-1 md:p-1.5 text-emerald-200 hover:text-white hover:bg-emerald-800/50 rounded-full cursor-pointer"
                      >
                        <Plus size={10} />
                      </button>
                    </div>

                    <p className="text-sm md:text-base font-serif font-bold text-white tracking-tight">
                      {(cartItem.price * cartItem.quantity).toLocaleString()} <span className="text-[9px] font-mono text-[#D4AF37]">{currency}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center py-16 text-emerald-600/50">
              <Briefcase size={50} strokeWidth={1.2} className="mb-4" />
              <p className="text-[10px] font-mono uppercase tracking-[0.4em]">Portfolio vazio</p>
            </div>
          )}
        </div>

        {/* Footer Back Button */}
        <div className="p-6 md:p-8 bg-[#093829] border-t border-emerald-800/40">
          <button 
            onClick={() => setIsCartOpen(false)}
            className="flex items-center gap-3 text-[#D4AF37] text-[10px] font-mono font-bold uppercase tracking-[0.3em] group cursor-pointer hover:text-white transition-colors"
          >
            <ArrowLeft size={14} className="group-hover:-translate-x-1.5 transition-transform" /> Continuar explorando serviços
          </button>
        </div>
      </div>

      {/* RIGHT COLUMN: Client Protocol & Checkout */}
      <div className="lg:flex-1 bg-[#06241A] p-6 md:p-10 lg:p-12 lg:flex lg:flex-col lg:overflow-y-auto">
        <h3 className="text-[10px] font-mono font-bold text-[#D4AF37] uppercase tracking-[0.3em] mb-6 md:mb-8">
          Protocolo de Requisição do Cliente
        </h3>

        <div className="flex-1 space-y-6 md:space-y-8">
          
          {/* CLIENT INPUTS (PRESERVED) */}
          <div className="grid grid-cols-1 gap-3 md:gap-4">
            <div className="group">
              <label className="text-[9px] font-mono uppercase text-emerald-300/70 ml-2 mb-1.5 block tracking-wider">
                Entidade / Nome do Cliente
              </label>
              <input 
                placeholder="Ex: Global Solutions LDA" 
                value={client.name} 
                onChange={e => setClient({ ...client, name: e.target.value })} 
                className="w-full px-5 py-3.5 bg-[#093829] border border-emerald-800/80 rounded-xl text-xs md:text-sm text-white placeholder-emerald-700 focus:border-[#D4AF37] outline-none transition-all" 
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              <div>
                <label className="text-[9px] font-mono uppercase text-emerald-300/70 ml-2 mb-1.5 block tracking-wider">
                  Email
                </label>
                <input 
                  placeholder="Email" 
                  type="email" 
                  value={client.email} 
                  onChange={e => setClient({ ...client, email: e.target.value })} 
                  className="w-full px-5 py-3.5 bg-[#093829] border border-emerald-800/80 rounded-xl text-xs md:text-sm text-white placeholder-emerald-700 focus:border-[#D4AF37] outline-none transition-all" 
                />
              </div>
              <div>
                <label className="text-[9px] font-mono uppercase text-emerald-300/70 ml-2 mb-1.5 block tracking-wider">
                  Telefone
                </label>
                <input 
                  type='number' 
                  placeholder="Phone" 
                  value={client.phone} 
                  onChange={e => setClient({ ...client, phone: e.target.value })} 
                  className="w-full px-5 py-3.5 bg-[#093829] border border-emerald-800/80 rounded-xl text-xs md:text-sm text-white placeholder-emerald-700 focus:border-[#D4AF37] outline-none transition-all" 
                />
              </div>
            </div>

            <div>
              <label className="text-[9px] font-mono uppercase text-emerald-300/70 ml-2 mb-1.5 block tracking-wider">
                Notas especificas
              </label>
              <textarea 
                placeholder="Specific case notes..." 
                rows={3} 
                value={client.notes || ""} 
                onChange={e => setClient({ ...client, notes: e.target.value })} 
                className="w-full px-5 py-3.5 bg-[#093829] border border-emerald-800/80 rounded-xl text-xs md:text-sm text-white placeholder-emerald-700 focus:border-[#D4AF37] outline-none transition-all resize-none" 
              />
            </div>
          </div>

          {/* PAYMENT METHODS */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
              <label className="text-[10px] font-mono font-bold uppercase tracking-wider text-emerald-200">
                métodos de pagamento
              </label>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              {['mpesa', 'visa', 'emola', 'transfer'].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => { setPaymentMethod(m as any); setMobileMoneyPhone(''); }}
                  className={`py-3.5 rounded-xl text-[10px] font-mono font-bold uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer ${
                    paymentMethod === m 
                      ? 'bg-[#D4AF37] text-black border border-[#D4AF37] shadow-lg scale-[1.02]' 
                      : 'bg-[#093829] text-emerald-200 border border-emerald-800/60 hover:border-emerald-600'
                  }`}
                >
                  {m === 'emola' ? 'e-Mola' : m === 'transfer' ? 'Bank Transfer' : m}
                  {paymentMethod === m && <Check size={12} strokeWidth={3} />}
                </button>
              ))}
            </div>

            {/* Número de Telemóvel para M-Pesa / E-Mola (PRESERVED) */}
            {(paymentMethod === 'mpesa' || paymentMethod === 'emola') && (
              <div className="flex flex-col gap-1.5 pt-2">
                <input
                  type="tel"
                  value={mobileMoneyPhone}
                  onChange={e => setMobileMoneyPhone(e.target.value)}
                  placeholder={paymentMethod === 'mpesa' ? '+258 84 XXX XXXX' : '+258 86 XXX XXXX'}
                  className="w-full px-5 py-3.5 bg-[#093829] border border-emerald-800/80 rounded-xl text-xs text-white placeholder-emerald-700 focus:border-[#D4AF37] outline-none transition-all"
                />
                <p className="text-[9px] font-mono text-emerald-400/60 ml-2">
                  {paymentMethod === 'mpesa'
                    ? 'O número deve começar com 84 ou 85'
                    : 'O número deve começar com 86 ou 87'}
                </p>
              </div>
            )}
          </div>

          {/* BANK ACCOUNTS (PRESERVED) */}
          {paymentMethod === 'transfer' && (
            <div className="animate-in fade-in duration-300 p-5 bg-[#093829] text-white rounded-2xl border border-emerald-800/80 shadow-xl">
              <h4 className="text-[10px] font-mono font-bold uppercase tracking-wider mb-4 flex items-center gap-2 text-[#D4AF37]">
                <CreditCard size={14} /> Dados da Conta Bancária para Transferência
              </h4>
              <div className="space-y-4">
                {company.bankAccounts?.map((bank, idx) => (
                  <div key={idx} className="border-b border-emerald-800/60 pb-3 last:border-0 last:pb-0">
                    <p className="text-[10px] font-mono font-bold uppercase mb-2 text-emerald-300">{bank.bankName}</p>
                    <div className="grid grid-cols-2 gap-3 text-[10px] font-mono text-emerald-100/70">
                      <div><p className="text-[8px] uppercase text-emerald-400/50 mb-0.5">Account</p><p className="font-bold">{bank.accountNumber}</p></div>
                      <div><p className="text-[8px] uppercase text-emerald-400/50 mb-0.5">NIB/IBAN</p><p className="font-bold">{bank.nibOrIban}</p></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* GRAND TOTAL & SUBMIT BUTTONS (PRESERVED LOGIC) */}
        <div className="mt-8 pt-6 border-t border-emerald-900/80">
          <div className="flex justify-between items-end mb-6">
            <div>
              <span className="text-[9px] font-mono text-emerald-400/60 uppercase tracking-widest block mb-1">
                Total
              </span>
              <div className="text-3xl md:text-4xl font-serif font-bold text-white tracking-tight">
                {totals.grandTotal.toLocaleString()} <span className="text-sm font-mono text-[#D4AF37] font-normal">{currency}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <button
              onClick={handlePayment}
              disabled={!paymentMethod || !['mpesa', 'visa', 'emola'].includes(paymentMethod) || ((paymentMethod === 'mpesa' || paymentMethod === 'emola') && !mobileMoneyPhone)}
              className="w-full py-4 bg-[#D4AF37] hover:bg-[#C5A059] text-black text-[10px] font-mono font-bold uppercase tracking-[0.25em] rounded-full transition-all shadow-xl disabled:opacity-25 disabled:cursor-not-allowed active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
            >
              <Lock size={12} /> 
              {hasOrderWithPrice ? `Pagamento Seguro (${totals.grandTotal.toLocaleString()})` : 'Inicializar Gateway'}
            </button>

            <button
              onClick={handleSubmit}
              disabled={submitting || !client.name || !client.email || (hasOrderWithPrice && ['mpesa', 'visa', 'emola'].includes(paymentMethod))}
              className="w-full py-4 bg-[#093829] hover:bg-emerald-900 text-white border border-emerald-700/60 text-[10px] font-mono font-bold uppercase tracking-[0.25em] rounded-full transition-all disabled:opacity-25 disabled:cursor-not-allowed active:scale-95 shadow-md cursor-pointer"
            >
              {paymentMethod === 'transfer' ? 'Confirm Transfer Receipt' : 'Finalize Engagement Request'}
            </button>
          </div>
        </div>

      </div>

    </div>
  </div>
</div>

     {/* SUCCESS MODAL */}
{showSuccessModal && (
  <div 
    className="fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-6 bg-[#06241A]/80 backdrop-blur-md animate-in fade-in duration-500" 
    onClick={closeSuccessModal}
  >
    <div
      className="bg-[#06241A] text-white shadow-[0_30px_100px_rgba(0,0,0,0.8)] max-w-sm md:max-w-md w-full relative overflow-hidden transition-all transform scale-100 rounded-3xl border border-emerald-800/60"
      onClick={e => e.stopPropagation()}
    >
      {/* Top Gold Bar Accent */}
      <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-[#D4AF37] via-emerald-400 to-[#D4AF37]" />

      <div className="p-8 md:p-10 text-center">
        {/* Success Icon */}
        <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-[#093829] rounded-2xl mb-6 border border-[#D4AF37]/30 shadow-2xl relative group">
          <div className="absolute inset-0 bg-[#D4AF37]/10 rounded-2xl blur-md" />
          <div className="relative w-10 h-10 md:w-12 md:h-12 bg-[#D4AF37] rounded-xl flex items-center justify-center text-[#06241A] shadow-lg">
            <Check size={22} strokeWidth={3.5} />
          </div>
        </div>

        {/* Text Content */}
        <div className="space-y-3 mb-8">
          <span className="text-[10px] font-mono font-bold uppercase tracking-[0.3em] text-[#D4AF37] block">
            Solicitação de Requisição Registrada
          </span>

          <h2 className="text-2xl md:text-3xl font-serif font-bold text-white leading-tight tracking-tight">
            Requisição <span className="text-[#D4AF37] italic font-serif lowercase">Processada</span> com sucesso
          </h2>

          <p className="text-emerald-100/70 text-xs md:text-sm leading-relaxed max-w-[300px] mx-auto font-light">
            Sua solicitação foi registrada. Um e-mail de confirmação foi enviado para o seu endereço profissional.
          </p>
        </div>

        {/* Action Button */}
        <button
          onClick={closeSuccessModal}
          className="w-full py-4 bg-[#D4AF37] hover:bg-[#C5A059] text-black font-mono font-bold text-xs uppercase tracking-[0.25em] rounded-full transition-all shadow-xl active:scale-95 mb-6 md:mb-8 cursor-pointer"
        >
          Voltar ao Painel
        </button>

        {/* Reference Protocol Card */}
        <div className="pt-5 border-t border-emerald-900/80 flex flex-col items-center gap-2">
          <p className="text-[9px] font-mono uppercase tracking-[0.2em] text-emerald-400/60">
            Referência de Protocolo do Cliente
          </p>
          <span className="font-mono text-xs md:text-sm font-bold text-[#D4AF37] tracking-[0.25em] bg-[#093829] px-4 py-1.5 rounded-lg border border-emerald-800/80 shadow-inner">
            #AX-{Math.random().toString(36).substr(2, 5).toUpperCase()}
          </span>
        </div>
      </div>
    </div>
  </div>
)}

      {/* FOOTER */}
<footer className="bg-gradient-to-b from-[#06241A] to-[#041912] text-white pt-20 md:pt-32 pb-8 md:pb-12 overflow-hidden relative border-t border-emerald-900/60">
  {/* Subtle Grid Overlay */}
  <div 
    className="absolute inset-0 opacity-[0.03] pointer-events-none" 
    style={{ backgroundImage: 'radial-gradient(#D4AF37 1px, transparent 1px)', backgroundSize: '40px 40px' }} 
  />

  <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10 md:gap-16 mb-20 md:mb-28">
      
      {/* BRAND & NEWSLETTER COLUMN */}
      <div className="sm:col-span-2 lg:col-span-4 space-y-8 md:space-y-10">
        <div className="flex items-center">
          {company.logo ? (
            <img src={company.logo} alt={company.name} className="h-8 md:h-10 w-auto brightness-0 invert" />
          ) : (
            <span className="font-serif font-bold text-xl md:text-2xl tracking-tight text-white uppercase">
              {company.name}<span className="text-[#D4AF37]">.</span>
            </span>
          )}
        </div>

        <p className="text-emerald-100/60 text-xs md:text-sm leading-relaxed max-w-xs font-light">
          Líder global em inteligência jurídica estratégica e advocacia de alta complexidade. 
          Desenhando soluções corporativas com precisão e excelência absoluta.
        </p>

        <div className="space-y-3 md:space-y-4 pt-2">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
            <p className="text-[9px] md:text-[10px] font-mono font-bold uppercase tracking-[0.3em] text-[#D4AF37]">
              Acesso ao Briefing Jurídico
            </p>
          </div>
          <div className="flex p-1.5 bg-[#093829]/60 border border-emerald-800/80 rounded-2xl focus-within:border-[#D4AF37] transition-all backdrop-blur-sm">
            <input 
              type="email" 
              placeholder="Acesso ao email corporativo" 
              className="flex-1 px-4 md:px-5 bg-transparent text-white text-xs outline-none placeholder:text-emerald-600 font-mono min-w-0" 
            />
            <button className="px-5 md:px-7 py-3 bg-[#D4AF37] hover:bg-[#C5A059] text-black rounded-xl text-[9px] md:text-[10px] font-mono font-bold uppercase tracking-widest transition-all shadow-xl whitespace-nowrap cursor-pointer">
              Subscribe
            </button>
          </div>
        </div>
      </div>

      {/* PRACTICE AREAS / OPERATIONS COLUMN */}
      <div className="lg:col-span-2 space-y-8 md:space-y-10">
        <h4 className="text-[9px] md:text-[10px] font-mono font-bold uppercase tracking-[0.3em] text-[#D4AF37]">
          Practices
        </h4>
        <ul className="space-y-3.5 md:space-y-4">
          {['Corporate Law', 'Arbitration', 'Tax & Legal', 'Compliance'].map(link => (
            <li key={link}>
              <a href="#" className="text-[10px] md:text-[11px] text-emerald-200/50 hover:text-white transition-all font-mono uppercase tracking-wider flex items-center gap-2 group">
                <span className="w-0 group-hover:w-3 h-[1px] bg-[#D4AF37] transition-all" />
                {link}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* DIRECT CONTACT COLUMN */}
      <div className="lg:col-span-3 space-y-8 md:space-y-10">
        <h4 className="text-[9px] md:text-[10px] font-mono font-bold uppercase tracking-[0.3em] text-[#D4AF37]">
          Contato Direto
        </h4>
        <ul className="space-y-6 md:space-y-8">
          <li className="flex flex-col gap-1 md:gap-1.5">
            <span className="text-[8px] md:text-[9px] text-emerald-400/50 font-mono uppercase tracking-[0.25em]">Linha Direta</span>
            <a href={`tel:${company.phone}`} className="text-white font-serif font-bold text-lg md:text-xl hover:text-[#D4AF37] transition-colors tracking-tight">
              {company.phone}
            </a>
          </li>
          <li className="flex flex-col gap-1 md:gap-1.5">
            <span className="text-[8px] md:text-[9px] text-emerald-400/50 font-mono uppercase tracking-[0.25em]">Conselheiro Oficial</span>
            <a href={`mailto:${company.email}`} className="text-emerald-200/80 font-mono text-xs md:text-sm hover:text-[#D4AF37] transition-colors tracking-tight break-all">
              {company.email}
            </a>
          </li>
        </ul>
      </div>

      {/* SOCIALS & PROTOCOLS COLUMN */}
      <div className="lg:col-span-3 space-y-8 md:space-y-10">
        <div>
          <h4 className="text-[9px] md:text-[10px] font-mono font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-5 md:mb-6">
            Conectar
          </h4>
          <div className="flex gap-3">
            {[Instagram, Facebook, Linkedin].map((Icon, idx) => (
              <a 
                key={idx} 
                href="#" 
                className="w-10 h-10 md:w-11 md:h-11 bg-[#093829] border border-emerald-800/80 rounded-xl flex items-center justify-center text-emerald-300 hover:text-black hover:bg-[#D4AF37] hover:border-[#D4AF37] transition-all duration-300 shadow-md"
              >
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-[9px] font-mono font-bold uppercase tracking-[0.25em] text-emerald-400/50">
            Métodos de Pagamento
          </p>
          <div className="flex flex-wrap gap-2">
            {['M-PESA', 'VISA', 'E-MOLA'].map(p => (
              <div key={p} className="px-3 py-1.5 border border-emerald-800/60 bg-[#093829]/40 rounded-lg text-[8px] md:text-[9px] font-mono font-bold text-emerald-200/70 hover:border-[#D4AF37]/50 transition-all">
                {p}
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>

    {/* SUB FOOTER */}
    <div className="pt-8 md:pt-10 border-t border-emerald-900/60 flex flex-col md:flex-row justify-between items-center gap-6 md:gap-8">
      <div className="space-y-2 text-center md:text-left">
        <p className="text-[9px] md:text-[10px] font-mono text-emerald-300/80 uppercase tracking-[0.25em]">
          © 2026 {company.name} <span className="text-emerald-600">/ Serviços Legais</span>
        </p>
        <div className="flex items-center justify-center md:justify-start gap-3 text-[8px] md:text-[9px] text-emerald-500 font-mono uppercase tracking-widest">
          <span>ISO 9001 CERTIFIED</span>
          <span className="w-1 h-1 rounded-full bg-emerald-700" />
          <span>GDPR COMPLIANT</span>
        </div>
      </div>

      <div className="flex gap-6 md:gap-8 font-mono">
        <a href="#" className="text-[8px] md:text-[9px] font-bold text-emerald-400/60 hover:text-[#D4AF37] transition-colors uppercase tracking-[0.25em]">Privacy Policy</a>
        <a href="#" className="text-[8px] md:text-[9px] font-bold text-emerald-400/60 hover:text-[#D4AF37] transition-colors uppercase tracking-[0.25em]">Terms of Engagement</a>
      </div>

      <div className="flex items-center gap-3 px-5 py-2.5 bg-[#093829]/60 rounded-xl border border-emerald-800/80 backdrop-blur-md">
        <div className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D4AF37] opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#D4AF37]"></span>
        </div>
        <span className="text-[9px] md:text-[10px] font-mono font-bold text-emerald-200 uppercase tracking-[0.2em]">
          System Status: Operational
        </span>
      </div>
    </div>
  </div>
</footer>
    
    {/* AWAITING CONFIRMATION MODAL */}
{showAwaitingConfirmation && (
  <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-[#06241A]/80 backdrop-blur-md animate-in fade-in duration-300">
    <div className="bg-[#06241A] text-white rounded-3xl p-8 md:p-10 max-w-md w-full border border-emerald-800/80 shadow-[0_30px_90px_rgba(0,0,0,0.8)] relative overflow-hidden text-center">
      
      {/* Accent Line */}
      <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-[#D4AF37] via-emerald-400 to-[#D4AF37]" />

      {/* STATE: WAITING */}
      {pollStatus === 'waiting' && (
        <div className="space-y-6">
          <div className="relative w-20 h-20 mx-auto flex items-center justify-center">
            {/* Outer Spinning Ring */}
            <div className="absolute inset-0 rounded-full border-2 border-[#D4AF37]/20 border-t-[#D4AF37] animate-spin" />
            {/* Inner Glow Badge */}
            <div className="w-12 h-12 bg-[#093829] rounded-2xl border border-emerald-700/50 flex items-center justify-center shadow-lg animate-pulse">
              <div className="w-2.5 h-2.5 rounded-full bg-[#D4AF37]" />
            </div>
          </div>

          <div className="space-y-2">
            <span className="text-[9px] font-mono font-bold uppercase tracking-[0.3em] text-[#D4AF37] block">
              Autorização Necessária
            </span>
            <h3 className="text-xl md:text-2xl font-serif font-bold text-white tracking-tight">
              Aguardando Confirmação
            </h3>
            <p className="text-xs md:text-sm text-emerald-100/70 leading-relaxed font-light max-w-xs mx-auto">
              Um pedido de pagamento foi enviado para o seu telemóvel.<br />
              Introduza o seu PIN no telefone para autorizar o pagamento.
            </p>
          </div>

          {awaitingRef && (
            <div className="pt-2">
              <p className="font-mono text-xs text-[#D4AF37] bg-[#093829] border border-emerald-800/80 inline-block px-4 py-1.5 rounded-full shadow-inner tracking-wider">
                Ref: {awaitingRef}
              </p>
            </div>
          )}
        </div>
      )}

      {/* STATE: CONFIRMED */}
      {pollStatus === 'confirmed' && (
        <div className="space-y-6 animate-in zoom-in-95 duration-300">
          <div className="w-16 h-16 md:w-20 md:h-20 mx-auto rounded-2xl bg-[#093829] border border-[#D4AF37]/40 flex items-center justify-center shadow-2xl relative">
            <div className="absolute inset-0 bg-[#D4AF37]/10 rounded-2xl blur-sm" />
            <CheckCircle className="w-8 h-8 md:w-10 md:h-10 text-[#D4AF37] relative z-10" strokeWidth={2.5} />
          </div>

          <div className="space-y-2">
            <span className="text-[9px] font-mono font-bold uppercase tracking-[0.3em] text-[#D4AF37] block">
              Protocolo Aprovado
            </span>
            <h3 className="text-xl md:text-2xl font-serif font-bold text-white tracking-tight">
              Pagamento Confirmado!
            </h3>
            <p className="text-xs text-emerald-200/60 font-mono uppercase tracking-widest animate-pulse">
              Redirecionando...
            </p>
          </div>
        </div>
      )}

      {/* STATE: FAILED */}
      {pollStatus === 'failed' && (
        <div className="space-y-6 animate-in zoom-in-95 duration-300">
          <div className="w-16 h-16 md:w-20 md:h-20 mx-auto rounded-2xl bg-red-950/40 border border-red-500/30 flex items-center justify-center shadow-2xl">
            <X className="w-8 h-8 md:w-10 md:h-10 text-red-400" strokeWidth={2.5} />
          </div>

          <div className="space-y-2">
            <span className="text-[9px] font-mono font-bold uppercase tracking-[0.3em] text-red-400 block">
              Transação Recusada
            </span>
            <h3 className="text-xl md:text-2xl font-serif font-bold text-white tracking-tight">
              Pagamento não confirmado
            </h3>
            <p className="text-xs md:text-sm text-emerald-100/60 font-light">
              O pagamento foi recusado ou expirou.
            </p>
          </div>

          <button
            onClick={() => setShowAwaitingConfirmation(false)}
            className="w-full py-4 bg-[#D4AF37] hover:bg-[#C5A059] text-black font-mono font-bold text-xs uppercase tracking-[0.25em] rounded-full transition-all shadow-xl active:scale-95 cursor-pointer"
          >
            Tentar novamente
          </button>
        </div>
      )}

    </div>
  </div>
)}
</div>
  );
};

export default SeiaPortal;
